/**
 * SoleadLab 2.0 - Map Canvas
 *
 * Core map component using Mapbox GL JS with:
 * - Satellite imagery base layer
 * - Mouse position tracking
 * - MapboxDraw for draggable/resizable indication polygons
 * - Layer management for overlays and indications
 *
 * REFERENCE: editor (23).html - uses MapboxDraw v1.5.0 for indication manipulation
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import type { DrawCustomMode } from '@mapbox/mapbox-gl-draw';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { useMapStore, useProjectStore, useUIStore, INDICATION_TYPES, STATUS_LABELS } from '../../stores';
import type { IndicationType } from '../../stores/projectStore';
import { useAuthStore } from '../../stores/authStore';
import { useDetectionStore } from '../../stores/detectionStore';
import { useIsShareViewer, useFieldCapabilities, useShareSectionAllowed } from '../../hooks/useShareViewer';
import { useShareSessionStore } from '../../stores/shareSessionStore';
import useManualOrthoMarkers from './useManualOrthoMarkers';
import useOverlayMarkers from './useOverlayMarkers';
import useOverlayEdit from './useOverlayEdit';
import { useBatchMove } from './useBatchMove';
import { useBatchDelete } from './useBatchDelete';
import useDataPointMarkers from './useDataPointMarkers';
import useDataPointPlacement from './useDataPointPlacement';
import useLayoutOverlay from './useLayoutOverlay';
import useDxfLayers from './useDxfLayers';
import useDxfAdjust from './useDxfAdjust';
import useDxfZones from './useDxfZones';
import useIVOverlay from './useIVOverlay';
import { reapplyTopologyForIndication, computeTopologyUpdates } from '../../domain/topology/reapplyTopology';
import { captureDay } from '../../utils/captureDate';
import { observedOnVisibleDay } from '../../utils/observations';
import { centroidOfPolygon, indCenter } from '../../utils/geometry';
import { pointInPolygon } from '../../utils/missionValidation';
import { quickCreateIndication } from '../../utils/createIndication';
import { handleFieldQuickAddCreated } from '../../utils/fieldPhotoFlow';
import { canRenderBakedTiles } from '../../utils/planBake';
import { overlayRasterOpacity } from '../../utils/overlayPaint';
import FlightPathPopup from './FlightPathPopup';
import MapPointDetailPopup from './MapPointDetailPopup';
import DetectionGhostPopup from '../detection/DetectionGhostPopup';
import GuideHud from './GuideHud';
import { buildTileUrl } from '../../services/tileApi';
import { resolveSignedUrls } from '../../services/signedAssetUrl';
import LocationSearch from './LocationSearch';
import './MapCanvas.css';

// Mapbox access token — re-exported via services/mapboxToken so the dashboard
// map and any future map surfaces share one source of truth.
import { MAPBOX_TOKEN } from '../../services/mapboxToken';

// Load RTL text plugin for Hebrew/Arabic label rendering (lazy-loaded)
mapboxgl.setRTLTextPlugin(
  'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.3.0/mapbox-gl-rtl-text.js',
  null,
  true,
);

// Compute bearing (degrees, 0=north, clockwise) between two [lng, lat] coordinates
function computeBearing(from: number[], to: number[]): number {
  const toRad = Math.PI / 180;
  const toDeg = 180 / Math.PI;
  const φ1 = from[1] * toRad;
  const φ2 = to[1] * toRad;
  const Δλ = (to[0] - from[0]) * toRad;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * toDeg) + 360) % 360;
}

// Create a triangle image (pointing up by default) for flight path direction markers
function createTriangleImage(color: string, size: number = 20): ImageData {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  // Draw triangle pointing UP (north) — Mapbox icon-rotate will rotate it
  const cx = size / 2;
  const top = 2;
  const bottom = size - 2;
  const halfBase = size / 2 - 2;

  ctx.beginPath();
  ctx.moveTo(cx, top);              // top vertex
  ctx.lineTo(cx + halfBase, bottom); // bottom-right
  ctx.lineTo(cx - halfBase, bottom); // bottom-left
  ctx.closePath();

  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  return ctx.getImageData(0, 0, size, size);
}

// Wedge for the GPS heading cone. Apex at the canvas center (the rotation
// pivot = the user dot), opening upward — Mapbox icon-rotate spins it to the
// course over ground. ORANGE with a white edge, deliberately NOT blue: it must
// stand apart from the blue dot + accuracy halo and stay loud on a grayscale
// thermal ortho (field-smoke feedback — the blue-on-blue version was invisible).
function createHeadingConeImage(size: number = 128): ImageData {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const c = size / 2;
  const r = c - 3;
  const halfAngle = (30 * Math.PI) / 180;
  const grad = ctx.createRadialGradient(c, c, 2, c, c, r);
  grad.addColorStop(0, 'rgba(255, 140, 0, 0.95)');
  grad.addColorStop(0.6, 'rgba(255, 140, 0, 0.55)');
  grad.addColorStop(1, 'rgba(255, 140, 0, 0.15)');
  ctx.beginPath();
  ctx.moveTo(c, c);
  ctx.arc(c, c, r, -Math.PI / 2 - halfAngle, -Math.PI / 2 + halfAngle);
  ctx.closePath();
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
  ctx.lineWidth = 2.5;
  ctx.lineJoin = 'round';
  ctx.stroke();
  return ctx.getImageData(0, 0, size, size);
}

// Green ✓ badge for RESOLVED faults (Build B3). Drawn on a canvas rather than a
// Mapbox text glyph, because the style's font stack doesn't carry U+2713 and the
// text renders blank. White halo underneath so it reads on any fault colour.
function createCheckImage(size: number = 28): ImageData {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const p = (x: number, y: number): [number, number] => [x * size, y * size];
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(...p(0.22, 0.55));
  ctx.lineTo(...p(0.42, 0.74));
  ctx.lineTo(...p(0.80, 0.28));
  // White halo, then the green check on top.
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = size * 0.24;
  ctx.stroke();
  ctx.strokeStyle = '#16a34a';
  ctx.lineWidth = size * 0.14;
  ctx.stroke();
  return ctx.getImageData(0, 0, size, size);
}

// Ensure correct layer order: ortho < overlay-PNG < flight-paths < data-points < draw (indications) < resolved-✓ < user-location (GPS dot always on top)
// Called after adding any raster (overlay or ortho) layer so draw layers stay visible.
function ensureLayerOrder(map: mapboxgl.Map): void {
  try {
    const style = map.getStyle();
    if (!style?.layers) return;

    // Find the first gl-draw layer to insert our custom layers below it
    const firstDrawId = style.layers.find(
      (l) => l.id.startsWith('gl-draw-')
    )?.id;

    // Step 1: move ortho raster layers above Mapbox labels (below draw layers).
    // Stack by the store's array order (orthophotos[0] = top). Iterating in
    // REVERSE and moving each just before the first draw layer leaves index 0
    // moved last = closest to draw = topmost. This makes array order the source
    // of truth for stacking, so user reordering (moveOrthophoto) re-stacks
    // existing layers — not only newly-added ones.
    const orthosTopFirst = useProjectStore.getState().data.orthophotos;
    for (let idx = orthosTopFirst.length - 1; idx >= 0; idx--) {
      const orthoLayerId = `ortho-layer-${orthosTopFirst[idx].id}`;
      if (map.getLayer(orthoLayerId)) {
        try { map.moveLayer(orthoLayerId, firstDrawId); } catch (_e) { /* ignore */ }
      }
    }
    // Step 1.5: move overlay PNG layers above orthos
    style.layers.forEach((layer) => {
      if (layer.id.startsWith('overlay-layer-')) {
        try { map.moveLayer(layer.id, firstDrawId); } catch (_e) { /* ignore */ }
      }
    });
    // Step 2: move layout layers above overlays.
    //
    // F-01C: per-layout layer IDs (`layout-image-layer-<sanitized>`,
    // `layout-labels-layer-<sanitized>`, etc.) — iterate the style with
    // prefix checks instead of looking up singleton IDs. The contract is
    // identical to the pre-F-01C singleton model: image at the bottom of
    // the layout-overlay stack, then module-grid fill/outline/labels in
    // the middle, then text labels on top so they stay readable above the
    // orthophoto + the grid. Each forEach pass moves every matching layer
    // to the top, so by the time we're done the relative order of the
    // five passes is preserved across as many layouts as the project has.
    style.layers.forEach((layer) => {
      if (layer.id.startsWith('layout-image-layer-')) {
        try { map.moveLayer(layer.id); } catch (_e) { /* ignore */ }
      }
    });
    // Rung-3 DXF linework: above every raster (orthos, overlay PNGs, layout
    // image), below the module grid / string lines / labels so CAD lines
    // never bury the topology surface. Absent from these passes = sinks
    // below a recreated orthophoto (the WP-C string-lines production bug).
    // Fills before lines: same relative order as their creation.
    style.layers.forEach((layer) => {
      if (layer.id.startsWith('dxf-layer-fill-')) {
        try { map.moveLayer(layer.id); } catch (_e) { /* ignore */ }
      }
    });
    style.layers.forEach((layer) => {
      if (layer.id.startsWith('dxf-layer-line-')) {
        try { map.moveLayer(layer.id); } catch (_e) { /* ignore */ }
      }
    });
    // Zone selection highlight sits above its own linework.
    style.layers.forEach((layer) => {
      if (layer.id.startsWith('dxf-zone-highlight')) {
        try { map.moveLayer(layer.id); } catch (_e) { /* ignore */ }
      }
    });
    style.layers.forEach((layer) => {
      if (layer.id.startsWith('layout-module-grid-fill-')) {
        try { map.moveLayer(layer.id); } catch (_e) { /* ignore */ }
      }
    });
    style.layers.forEach((layer) => {
      if (layer.id.startsWith('layout-module-grid-outline-')) {
        try { map.moveLayer(layer.id); } catch (_e) { /* ignore */ }
      }
    });
    // Sharp-labels WP-C: string lines above the grid, their polarity signs
    // above the module numbers, text labels still on top. Missing from these
    // passes = the layer stays wherever creation left it, which in prod was
    // BELOW a later-recreated orthophoto (owner-reported).
    style.layers.forEach((layer) => {
      if (layer.id.startsWith('layout-string-lines-layer-')) {
        try { map.moveLayer(layer.id); } catch (_e) { /* ignore */ }
      }
    });
    style.layers.forEach((layer) => {
      if (layer.id.startsWith('layout-module-grid-labels-')) {
        try { map.moveLayer(layer.id); } catch (_e) { /* ignore */ }
      }
    });
    style.layers.forEach((layer) => {
      if (layer.id.startsWith('layout-string-polarity-')) {
        try { map.moveLayer(layer.id); } catch (_e) { /* ignore */ }
      }
    });
    style.layers.forEach((layer) => {
      if (layer.id.startsWith('layout-labels-layer-')) {
        try { map.moveLayer(layer.id); } catch (_e) { /* ignore */ }
      }
    });
    // Step 3: move flight path layers above overlays
    style.layers.forEach((layer) => {
      if (layer.id.startsWith('flight-path')) {
        try { map.moveLayer(layer.id); } catch (_e) { /* ignore */ }
      }
    });
    // Step 4: move data point markers above flight paths
    if (map.getLayer('data-points-marker')) {
      try { map.moveLayer('data-points-marker'); } catch (_e) { /* ignore */ }
    }
    // Step 4.5: move IV result overlay above data points
    if (map.getLayer('iv-result-fill')) {
      try { map.moveLayer('iv-result-fill'); } catch (_e) { /* ignore */ }
    }
    if (map.getLayer('iv-result-line')) {
      try { map.moveLayer('iv-result-line'); } catch (_e) { /* ignore */ }
    }
    if (map.getLayer('iv-result-text')) {
      try { map.moveLayer('iv-result-text'); } catch (_e) { /* ignore */ }
    }
    // Step 5: move draw layers (indications) above everything
    style.layers.forEach((layer) => {
      if (layer.id.startsWith('gl-draw-')) {
        try { map.moveLayer(layer.id); } catch (_e) { /* ignore */ }
      }
    });
    // Step 5.5: detection ghost overlay above the draw layers. Added once at
    // init, so without a re-raise here the orthos (anchored just below gl-draw
    // by Step 1) would cover the ghosts from the first reshuffle on. Above
    // draw so an accepted ghost's solid outline + ✓ stay readable over the
    // newly created indication's fill; the layers render nothing when the
    // detection source is empty, so always-on-top is safe.
    [
      'detection-ghost-fill',
      'detection-ghost-outline',
      'detection-accepted-outline',
      'detection-accepted-check',
    ].forEach((id) => {
      if (map.getLayer(id)) {
        try { map.moveLayer(id); } catch (_e) { /* ignore */ }
      }
    });
    // Step 6: the resolved ✓ badge sits ABOVE the indication fills — re-raise it
    // LAST, else Step 5 buries it under the fault polygon on every reshuffle
    // (this was the cause of the ✓ vanishing on a date/year switch).
    if (map.getLayer('resolved-badges')) {
      try { map.moveLayer('resolved-badges'); } catch (_e) { /* ignore */ }
    }
    // Step 7: field-navigation stack topmost. These layers are added once at
    // init and were never re-raised here, so from the second reshuffle on the
    // orthos (anchored just below gl-draw) ended up covering the dot —
    // invisible in the field. Sources are empty when tracking/guide is off, so
    // always-on-top is safe. Order (bottom→top): guide line → target pin →
    // accuracy halo → heading cone → dot.
    [
      'guide-line-casing',
      'guide-line',
      'guide-target-point',
      'user-location-accuracy',
      'user-location-heading',
      'user-location-dot',
    ].forEach((id) => {
      if (map.getLayer(id)) {
        try { map.moveLayer(id); } catch (_e) { /* ignore */ }
      }
    });
  } catch (_e) { /* ignore */ }
}

// Get indication color by type — single source of truth is INDICATION_TYPES
// (projectStore) so newly added types (Tracker/Vegetation/Custom/…) render with
// their real color instead of the generic gray fallback.
// Build B: resolved faults KEEP their type color — resolution is conveyed by the
// ✓ badge layer (Current mode), not by the old grey — so there is no longer a
// resolved-specific color here.
function getIndicationColor(type: string): string {
  return INDICATION_TYPES[type as IndicationType]?.color || '#888888';
}

// indCenter (representative lng/lat for an indication geometry) moved to
// utils/geometry.ts so guide-line entry points can share it.

// Fast geometry comparison — avoids O(n) JSON.stringify on every sync
function geometryChanged(a: GeoJSON.Geometry, b: GeoJSON.Geometry): boolean {
  if (a.type !== b.type) return true;
  if (a.type === 'Polygon' && b.type === 'Polygon') {
    const sa = a.coordinates[0], sb = b.coordinates[0];
    if (sa.length !== sb.length) return true;
    for (let i = 0; i < sa.length; i++) {
      if (sa[i][0] !== sb[i][0] || sa[i][1] !== sb[i][1]) return true;
    }
    return false;
  }
  if (a.type === 'Point' && b.type === 'Point') {
    return a.coordinates[0] !== b.coordinates[0] || a.coordinates[1] !== b.coordinates[1];
  }
  if (a.type === 'LineString' && b.type === 'LineString') {
    if (a.coordinates.length !== b.coordinates.length) return true;
    for (let i = 0; i < a.coordinates.length; i++) {
      if (a.coordinates[i][0] !== b.coordinates[i][0] || a.coordinates[i][1] !== b.coordinates[i][1]) return true;
    }
    return false;
  }
  // Fallback for rare geometry types
  return JSON.stringify(a) !== JSON.stringify(b);
}

// View-only select mode for the read-only share viewer. It is `simple_select`
// with every mutation path no-op'd: clicking an indication STILL selects it (so
// the read-only preview popup opens via draw.selectionchange), but drag-move,
// the drag/box-select setup, and delete are disabled. A visitor can inspect
// indications without moving or removing them. (The backend also rejects
// share-visitor writes — this removes the gesture entirely on the client.)
const baseDrawModes = (MapboxDraw as unknown as { modes: Record<string, DrawCustomMode> }).modes;
const stockSimpleSelect = baseDrawModes.simple_select as unknown as {
  onMouseUp: (state: unknown, e: unknown) => void;
  onTouchEnd: (state: unknown, e: unknown) => void;
  onTap: (state: unknown, e: unknown) => void;
  onClick: (state: unknown, e: unknown) => void;
  onDrag: (state: unknown, e: unknown) => void;
  onStop: (state: unknown) => void;
  clickOnFeature: (state: unknown, e: unknown) => void;
  startOnActiveFeature: (state: unknown, e: unknown) => void;
  stopExtendedInteractions: (state: unknown) => void;
};

// simple_select.toDisplayFeatures minus createSupplementaryPoints: selected
// features keep their 'active' styling but never sprout vertex handles.
// In the share modes, vertex editing exists only inside direct_select, which
// the field card's Reshape button enters explicitly.
function toDisplayFeaturesNoHandles(
  this: { isSelected: (id: string) => boolean; fireActionable: () => void },
  _state: unknown,
  geojson: { properties: { active?: string; id?: string } },
  display: (geojson: unknown) => void,
) {
  geojson.properties.active = geojson.properties.id && this.isSelected(geojson.properties.id)
    ? 'true'
    : 'false';
  display(geojson);
  this.fireActionable();
}

const ViewSelectMode = {
  ...baseDrawModes.simple_select,
  onMouseDown() { /* view-only: skip drag/box-select setup so the map still pans */ },
  onTouchStart() { /* view-only: as onMouseDown */ },
  onDrag() { /* view-only: features are not movable */ },
  onTrash() { /* view-only: deletion disabled */ },
  clickOnVertex() { /* view-only: no vertex editing (belt to the handle-free display) */ },
  toDisplayFeatures: toDisplayFeaturesNoHandles,
  // Stock simple_select enters direct_select (vertex editing) when an
  // already-selected polygon is clicked a second time — and leaving
  // direct_select lands in stock EDITABLE simple_select, so one re-click
  // used to hand a share visitor the full geometry-editing surface.
  // Delegate only the fresh-selection click (so draw.selectionchange still
  // opens the read-only preview) and swallow clicks on selected features.
  clickOnFeature(state: unknown, e: { featureTarget?: { properties?: { id?: string } } }) {
    const featureId = e.featureTarget?.properties?.id;
    const self = this as unknown as { isSelected: (id: string) => boolean };
    if (featureId && self.isSelected(featureId)) return;
    stockSimpleSelect.clickOnFeature.call(this, state, e);
  },
} as unknown as DrawCustomMode;

// FL-2.5: select mode for FIELD-kind share visitors. A field link owns what
// it created; the backend re-enforces ownership on every write, so all of
// this is UX gating, not security. Owner-ratified interaction model
// (2026-08-13): tap = select only (the field card opens via
// draw.selectionchange); LONG-PRESS an own marker = move mode (drag follows
// the finger, release commits); reshape/vertex editing is entered ONLY via
// the field card's explicit Reshape button (direct_select) — never from a
// tap, re-tap, or vertex hit.
type FeatureTargetEvent = {
  featureTarget?: { properties?: { id?: string } };
  point?: { x: number; y: number };
  lngLat?: mapboxgl.LngLat;
  originalEvent?: { touches?: { length: number } };
};
// Ownership is resolved from the STORE, by feature id — the same check the
// field card and setIndicationFieldGeometry use, so what the gesture allows
// and what the write path accepts can never disagree. Reading it off
// e.featureTarget instead would mean trusting whatever survives gl-draw's
// user_-prefixing AND Mapbox's tile encoding of a nested object, which is
// exactly the coupling that broke this predicate once already.
function isOwnFieldFeature(e: FeatureTargetEvent): boolean {
  const id = e.featureTarget?.properties?.id;
  if (!id) return false;
  const viewId = useShareSessionStore.getState().view?.id;
  if (!viewId) return false;
  const ind = useProjectStore.getState().data.indications.find((i) => i.id === id);
  return ind?.properties.createdVia?.linkId === viewId;
}

const FIELD_LONG_PRESS_MS = 550;
// Movement allowed during the hold before it is treated as a pan instead.
// The invariant is not the number 25 but "comfortably above the platform's own
// long-press allowance" (iOS UILongPressGestureRecognizer.allowableMovement is
// 10 pt). Never lower this below ~15 or real presses die again.
// A fingertip's contact centroid drifts several pixels while pressing, and
// gl-draw only starts routing drag events at 250 ms (is_tap.js: a release
// under 25 px AND 250 ms is a tap) — so every jitter sample in the second
// half of the hold reaches this check. A tight threshold cancels real
// long-presses; match gl-draw's own 25 px tap tolerance, which is the
// library's definition of "hasn't moved".
const FIELD_LONG_PRESS_SLOP_PX = 25;
let fieldHold: {
  timer: number;
  x: number;
  y: number;
  fired: boolean;
  ctx: unknown;
  state: unknown;
  featureTarget: unknown;
  // Most recent finger position. Panning stays enabled during the hold, so
  // the geographic point under the finger drifts; arming the drag with the
  // touchstart lngLat would make the mark jump by that drift on the first
  // drag sample.
  liveLngLat?: mapboxgl.LngLat;
} | null = null;
// gl-draw routes touchstart/touchmove/touchend to modes but NOT touchcancel,
// so an OS-interrupted hold (incoming call, notification shade, palm
// rejection) would otherwise fire the timer after the finger is gone — or,
// worse, strand an already-armed drag with map panning disabled until the
// next tap. A window listener (added per hold, removed when it ends) covers
// both: cancel a pending timer, unwind an armed drag.
// Unwind an armed (already-fired) hold when no release event will arrive to
// do it. If the feature was actually being dragged, its coordinates have
// already moved on the map — commit that through the normal update path
// rather than dropping it, or the map would show a move the store never saw.
function unwindArmedHold(ctx: unknown, state: unknown) {
  const dragState = state as { dragMoving?: boolean };
  const mode = ctx as { fireUpdate?: () => void; map?: mapboxgl.Map };
  if (dragState?.dragMoving) mode.fireUpdate?.();
  stockSimpleSelect.stopExtendedInteractions.call(ctx, state);
  // stopExtendedInteractions re-enables map panning ONLY when
  // state.initialDragPanState is true — a snapshot taken in onSetup that can
  // be stale. If it is false the map is left permanently un-pannable (pinch
  // zoom keeps working, which is exactly what a stuck dragPan looks like).
  // The hold is the only thing here that disables panning, so re-enable it
  // unconditionally on the way out.
  mode.map?.dragPan.enable();
}
function onFieldHoldTouchCancel() {
  if (fieldHold === null) return;
  if (fieldHold.fired) {
    // startOnActiveFeature already disabled dragPan and set canDragMove,
    // and no touchend is coming — undo the arm.
    unwindArmedHold(fieldHold.ctx, fieldHold.state);
  }
  endFieldHold();
}
// Clears the pending timer only. Safe to call before stock onTouchEnd/
// onMouseUp: those run stock's own stopExtendedInteractions AFTER firing
// draw.update for a real drag, and unwinding here would clear
// state.dragMoving and swallow the move's commit.
function endFieldHold() {
  if (fieldHold !== null) {
    window.clearTimeout(fieldHold.timer);
    fieldHold = null;
  }
  window.removeEventListener('touchcancel', onFieldHoldTouchCancel);
}
// Release path for gl-draw's tap/click dispatch. A release that gl-draw
// classifies as a tap (<250 ms, <25 px) or a click (<4 px, NO time cap) is
// routed to onTap/onClick and NEVER to onTouchEnd/onMouseUp (events.js:63-75,
// 104-122) — so without this the timer outlives the gesture and arms a
// silent drag 550 ms later. No drag can have happened on this path (it is
// under the movement tolerance by definition), so unwinding an already-fired
// hold here cannot swallow a legitimate move commit.
function releaseFieldHoldOnClick(ctx: unknown, state: unknown) {
  if (fieldHold?.fired) {
    unwindArmedHold(ctx, state);
  }
  endFieldHold();
}
// Hold still on an own marker until the timer fires → select it and arm the
// stock drag (startOnActiveFeature disables map pan and sets canDragMove, so
// stock onDrag moves the feature and release fires draw.update). Until the
// timer fires nothing is armed, so the map pans normally under the hold.
function armFieldLongPress(ctx: unknown, state: unknown, e: FeatureTargetEvent) {
  // A previous hold that already armed a drag must be unwound before its
  // record is dropped — otherwise nothing left knows dragPan is disabled.
  if (fieldHold?.fired) unwindArmedHold(fieldHold.ctx, fieldHold.state);
  endFieldHold();
  const featureId = e.featureTarget?.properties?.id;
  if (!featureId) return;
  window.addEventListener('touchcancel', onFieldHoldTouchCancel);
  fieldHold = {
    x: e.point?.x ?? 0,
    y: e.point?.y ?? 0,
    fired: false,
    ctx,
    state,
    featureTarget: e.featureTarget,
    liveLngLat: e.lngLat,
    timer: window.setTimeout(() => {
      if (fieldHold === null) return;
      // Keep the record (fired) so touchcancel can still unwind the drag.
      fieldHold.fired = true;
      const mode = ctx as {
        isSelected: (id: string) => boolean;
        setSelected: (id: string) => void;
        map: mapboxgl.Map;
      };
      // Stock onMouseDown refreshes this before every arm; the whole
      // dragPan-restore path in stopExtendedInteractions keys off it, and a
      // stale `false` is what strands the map un-pannable. Arming from a
      // timer bypasses stock's handler, so refresh it here.
      (state as { initialDragPanState?: boolean }).initialDragPanState =
        mode.map.dragPan.isEnabled();
      // dragMove moves the current selection — select before arming.
      if (!mode.isSelected(featureId)) mode.setSelected(featureId);
      // Arm from where the finger is NOW: startOnActiveFeature stores this as
      // dragMoveLocation, and the first drag sample moves the mark by
      // (current − stored). Handing it the 550 ms-old touchstart position
      // would teleport the mark by every pixel of drift and map pan that
      // happened during the press.
      stockSimpleSelect.startOnActiveFeature.call(ctx, state, {
        featureTarget: fieldHold.featureTarget,
        lngLat: fieldHold.liveLngLat ?? e.lngLat,
      });
      // Arming happens from a timer, outside any gl-draw event — nothing has
      // asked the store to repaint, so the mark would not visibly go active
      // and draw.selectionchange (which opens the field card) would not fire
      // until the next map paint. Flush both now, so the hold engages
      // visibly at the moment it engages.
      (ctx as { _ctx?: { store?: { render: () => void } } })._ctx?.store?.render();
      // iOS Safari implements no vibration API, so on the phones this feature
      // exists for the buzz never happens — without a visible cue the tech
      // cannot tell move mode engaged and lifts the finger to check.
      (navigator as { vibrate?: (ms: number) => void }).vibrate?.(30);
      useUIStore.getState().addNotification({
        type: 'info',
        title: 'Move mode — drag to reposition',
        duration: 1500,
      });
    }, FIELD_LONG_PRESS_MS),
  };
}

const FieldSelectMode = {
  ...baseDrawModes.simple_select,
  onMouseDown(state: unknown, e: FeatureTargetEvent) {
    if (!isOwnFieldFeature(e)) return; // pan, don't drag
    armFieldLongPress(this, state, e);
  },
  onTouchStart(state: unknown, e: FeatureTargetEvent) {
    if (!isOwnFieldFeature(e)) return;
    armFieldLongPress(this, state, e);
  },
  onDrag(state: unknown, e: FeatureTargetEvent) {
    if (fieldHold !== null && !fieldHold.fired) {
      // A second finger means a pinch, not a press — and e.point is the
      // centroid of ALL touches, so it lurches far past any slop the moment
      // one lands. End the hold rather than measure nonsense.
      if ((e.originalEvent?.touches?.length ?? 1) > 1) {
        endFieldHold();
      } else if (e.point) {
        const dx = e.point.x - fieldHold.x;
        const dy = e.point.y - fieldHold.y;
        if (dx * dx + dy * dy > FIELD_LONG_PRESS_SLOP_PX * FIELD_LONG_PRESS_SLOP_PX) {
          endFieldHold(); // wandered off — a pan, not a hold
        } else if (e.lngLat) {
          fieldHold.liveLngLat = e.lngLat; // keep the drag anchor current
        }
      }
    }
    stockSimpleSelect.onDrag.call(this, state, e);
  },
  onMouseUp(state: unknown, e: unknown) {
    // Released before the timer = a plain tap; after it, stock handles the
    // drag commit (fireUpdate) + stopExtendedInteractions either way.
    endFieldHold();
    stockSimpleSelect.onMouseUp.call(this, state, e);
  },
  onTouchEnd(state: unknown, e: unknown) {
    endFieldHold();
    stockSimpleSelect.onTouchEnd.call(this, state, e);
  },
  // gl-draw dispatches tap and click as separate handlers (object_to_mode
  // maps tap→onTap, click→onClick), and a release short enough to qualify
  // reaches ONLY these — never onTouchEnd/onMouseUp. Both must release the
  // hold or a plain tap leaves a timer that arms drag-move after the finger
  // is gone.
  onTap(state: unknown, e: unknown) {
    releaseFieldHoldOnClick(this, state);
    stockSimpleSelect.onTap.call(this, state, e);
  },
  onClick(state: unknown, e: unknown) {
    releaseFieldHoldOnClick(this, state);
    stockSimpleSelect.onClick.call(this, state, e);
  },
  onStop(state: unknown) {
    // Mode switched away mid-hold (e.g. the card's Reshape button) — the
    // armed drag would otherwise leave dragPan disabled.
    if (fieldHold?.fired) unwindArmedHold(this, state);
    endFieldHold();
    stockSimpleSelect.onStop.call(this, state);
  },
  onTrash() { /* deletion happens through the Field update card only */ },
  clickOnVertex() { /* reshape only via the field card's Reshape button */ },
  toDisplayFeatures: toDisplayFeaturesNoHandles,
  // Tap = select only, own or not: ViewSelectMode's guarded click delegates
  // fresh selections to stock (card opens via draw.selectionchange) and
  // swallows re-taps on the selected feature (stock would enter
  // direct_select there).
  clickOnFeature(state: unknown, e: FeatureTargetEvent) {
    (ViewSelectMode as unknown as {
      clickOnFeature: (state: unknown, e: unknown) => void;
    }).clickOnFeature.call(this, state, e);
  },
} as unknown as DrawCustomMode;

export default function MapCanvas() {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const drawRef = useRef<MapboxDraw | null>(null);
  const renderedOverlaysRef = useRef<Set<string>>(new Set());
  const overlayUrlsRef = useRef<Map<string, string>>(new Map()); // track URL per overlay to avoid needless recreate
  const renderedOrthosRef = useRef<Set<string>>(new Set());
  const orthoUrlsRef = useRef<Map<string, string>>(new Map()); // track URL per ortho to avoid needless recreate
  const tileSourceModeRef = useRef<Map<string, 'image' | 'tile'>>(new Map()); // track render mode per layer
  const syncingFromStoreRef = useRef(false);

  // F-02E: read-only share viewers get a non-editable map. `isShareViewerRef`
  // is read inside the once-only map-init effect (no re-init on change); the
  // effect below enforces/repairs the mode if viewer status settles or flips.
  const isShareViewer = useIsShareViewer();
  const isShareViewerRef = useRef(isShareViewer);
  isShareViewerRef.current = isShareViewer;
  // FL-2.5: FIELD-kind visitors get field_select (own-creation editing);
  // view-kind visitors keep view_select.
  const fieldCaps = useFieldCapabilities();
  const isFieldViewer = fieldCaps.canSetStatus;
  const isFieldViewerRef = useRef(isFieldViewer);
  isFieldViewerRef.current = isFieldViewer;
  useEffect(() => {
    const draw = drawRef.current;
    if (!draw) return;
    (draw as unknown as { changeMode: (m: string) => void }).changeMode(
      isShareViewer ? (isFieldViewer ? 'field_select' : 'view_select') : 'simple_select'
    );
  }, [isShareViewer, isFieldViewer]);

  // mapStore: data selectors (fine-grained)
  const isMapReady = useMapStore((s) => s.isMapReady);
  const areaSelectMode = useMapStore((s) => s.areaSelectMode);
  const center = useMapStore((s) => s.center);
  const zoom = useMapStore((s) => s.zoom);
  const bearing = useMapStore((s) => s.bearing);
  const pitch = useMapStore((s) => s.pitch);
  // mapStore: action selectors (stable refs)
  const setMap = useMapStore((s) => s.setMap);
  const setMapReady = useMapStore((s) => s.setMapReady);
  const setView = useMapStore((s) => s.setView);
  const setCursorPosition = useMapStore((s) => s.setCursorPosition);
  const setDraw = useMapStore((s) => s.setDraw);
  const selectFeature = useMapStore((s) => s.selectFeature);
  const toggleSelection = useMapStore((s) => s.toggleSelection);
  const clearSelection = useMapStore((s) => s.clearSelection);

  // Track Ctrl key state for multi-select
  const ctrlKeyRef = useRef(false);

  // True between a lasso's draw.create and its deferred finalization tick —
  // lets modechange distinguish "polygon completed" (keep armed until the
  // deferred block applies the selection) from "user canceled with Esc".
  const lassoFinishingRef = useRef(false);

  // Area-select mode: arm/disarm Draw's polygon mode. The draw.create handler
  // consumes the polygon as a selection lasso (share viewers never see the
  // toggle — the orientation/selection UI is hidden for them).
  useEffect(() => {
    const draw = drawRef.current;
    if (!draw) return;
    if (areaSelectMode) {
      draw.changeMode('draw_polygon');
    } else if (draw.getMode() === 'draw_polygon') {
      draw.changeMode('simple_select');
    }
  }, [areaSelectMode]);

  // projectStore: narrow data selectors (each array independent)
  const indications = useProjectStore((s) => s.data.indications);
  const overlays = useProjectStore((s) => s.data.overlays);
  const orthophotos = useProjectStore((s) => s.data.orthophotos);
  const flightPaths = useProjectStore((s) => s.data.flightPaths);
  const geoPhotos = useProjectStore((s) => s.data.geoPhotos);

  // Edit-mode preview swap: while an ortho/overlay is being adjusted, its baked
  // raster tiles can't follow the drag handles (tile sources have no
  // setCoordinates), so the render effects below force the draggable preview
  // image for the entity under edit and restore tiles on exit. Derived from
  // store state only — tileStatus is never touched, so the tile-status poller's
  // self-heal (which re-flips a wiped 'ready') has nothing to fight.
  const activeOrthophotoId = useMapStore((s) => s.activeOrthophotoId);
  const orthoEditMode = useMapStore((s) => s.orthoEditMode);
  const activeOverlayId = useMapStore((s) => s.activeOverlayId);
  const overlayAdjustMode = useMapStore((s) => s.overlayAdjustMode);
  const editingOrthoId = orthoEditMode ? activeOrthophotoId : null;
  // Only an ADJUSTING overlay trades its baked tiles for the draggable image
  // preview — selection alone (ticket row click, map click) keeps the tiles.
  // Mirrors editingOrthoId above.
  const adjustingOverlayId = overlayAdjustMode ? activeOverlayId : null;

  // Sync indications from store to MapboxDraw, respecting type filters
  const syncIndicationsToDrawRef = useRef<() => void>();
  syncIndicationsToDrawRef.current = useCallback(() => {
    const draw = drawRef.current;
    if (!draw || syncingFromStoreRef.current) return;

    const filters = useMapStore.getState().indicationFilters;
    const showResolved = useMapStore.getState().indicationShowResolved;
    const fieldLens = useMapStore.getState().indicationFieldLens;
    const viewMode = useMapStore.getState().indicationViewMode;
    const visibleDatesSet = new Set(useProjectStore.getState().visibleDates);
    const hasAnyDates = useProjectStore.getState().availableDates.length > 0;

    // Get current draw features
    const drawFeatures = draw.getAll().features;
    const drawIds = new Set(drawFeatures.map((f: GeoJSON.Feature) => String(f.id)));

    // Build set of IDs that SHOULD be on the map (type visible AND date visible)
    const visibleIndications = indications.filter((i) => {
      if (filters[i.type] === false) return false;
      // Hide resolved only in Current mode with Show-resolved off (declutter).
      // As-found mode always shows resolved faults (as the inspection first found them).
      if (viewMode === 'current' && !showResolved && i.properties.status === 'resolved') return false;
      // FL-1 review lens (server stamps properties.lastFieldChangeAt on every
      // field write): 'field' keeps only what the field touched, 'operator'
      // keeps only what it did not.
      if (fieldLens === 'field' && !i.properties.lastFieldChangeAt) return false;
      if (fieldLens === 'operator' && i.properties.lastFieldChangeAt) return false;
      // PFC-4B: a reviewed Photo is a hidden container — its fault children carry
      // the markers; don't render the container on top of them.
      if (i.type === 'Photo' && i.properties.reviewed) return false;
      // Date filter: only apply when dates exist; undated items always visible.
      // Observation-aware: a persistent fault shows on EVERY date it was observed
      // (not just its latest), so linking one across inspections keeps it visible
      // under each inspection's date.
      if (hasAnyDates && !observedOnVisibleDay(i, visibleDatesSet)) return false;
      return true;
    });
    const visibleIds = new Set(visibleIndications.map((i) => i.id));

    // Remove features that are no longer visible (deleted from store OR type filtered out)
    drawIds.forEach((id: string) => {
      if (!visibleIds.has(id)) {
        draw.delete(id);
      }
    });

    // Add or update visible features
    visibleIndications.forEach((indication) => {
      const existingFeature = draw.get(indication.id);
      // Build B: every fault renders in its TYPE color (resolution is conveyed by
      // the ✓ badge layer, not by greying). Spread color LAST so it always wins
      // over any stray property from indication.properties.
      const desiredColor = getIndicationColor(indication.type);

      if (!existingFeature) {
        // Add new feature
        draw.add({
          type: 'Feature',
          id: indication.id,
          geometry: indication.geometry as GeoJSON.Geometry,
          properties: {
            id: indication.id,
            ind_type: indication.type,
            ...indication.properties,
            color: desiredColor,
          },
        });
      } else {
        // Update existing feature if geometry, type, or resolved-color changed
        const currentType = (existingFeature.properties as any)?.ind_type;
        const currentColor = (existingFeature.properties as any)?.color;
        const typeChanged = currentType !== indication.type;
        const colorChanged = currentColor !== desiredColor;

        if (geometryChanged(indication.geometry as GeoJSON.Geometry, existingFeature.geometry) || typeChanged || colorChanged) {
          draw.delete(indication.id);
          draw.add({
            type: 'Feature',
            id: indication.id,
            geometry: indication.geometry as GeoJSON.Geometry,
            properties: {
              id: indication.id,
              ind_type: indication.type,
              ...indication.properties,
              color: desiredColor,
            },
          });
        }
      }
    });

    // Selection reconcile: the delete+add above silently drops re-synced
    // features from Draw's selection (no selectionchange fires). Prune the
    // store's multi-selection to ids still on the map, then re-assert it in
    // Draw so highlights survive geometry/status updates instead of leaving
    // phantom "N selected" state. Programmatic changeMode fires no events.
    {
      const st = useMapStore.getState();
      if (st.selectedFeatureIds.length > 0) {
        const kept = st.selectedFeatureIds.filter((id) => visibleIds.has(id));
        if (kept.length !== st.selectedFeatureIds.length) {
          st.setSelectedFeatureIds(kept);
        }
        if (kept.length > 0 && draw.getMode() === 'simple_select') {
          draw.changeMode('simple_select', { featureIds: kept });
        }
      }
    }

    // Build B: ✓ badges over resolved faults — Current mode only. visibleIndications
    // already respects type/date filters + Show-resolved, so the badge set inherits
    // them; As-found mode emits none (resolution is ignored there).
    const badgeFeatures: GeoJSON.Feature[] = [];
    if (viewMode === 'current') {
      for (const i of visibleIndications) {
        if (i.properties.status !== 'resolved') continue;
        const c = indCenter(i.geometry as GeoJSON.Geometry);
        if (c) badgeFeatures.push({ type: 'Feature', geometry: { type: 'Point', coordinates: c }, properties: {} });
      }
    }
    const badgeSource = mapRef.current?.getSource('resolved-badges') as mapboxgl.GeoJSONSource | undefined;
    if (badgeSource) {
      badgeSource.setData({ type: 'FeatureCollection', features: badgeFeatures });
      // Keep the ✓ above the indication (draw) fills. ensureLayerOrder also re-raises
      // it (Step 6) whenever the layer order is re-established on ortho changes.
      if (mapRef.current?.getLayer('resolved-badges')) {
        try { mapRef.current.moveLayer('resolved-badges'); } catch { /* best-effort */ }
      }
    }
  }, [indications]);

  // Initialize map and draw
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: center,
      zoom: zoom,
      bearing: bearing,
      pitch: pitch,
      attributionControl: false,
    });

    // Initialize MapboxDraw for indication polygon manipulation
    // Matching original editor styles: colored fill, BLACK outline, 0.40 opacity
    const drawStyles = [
      // ACTIVE POLYGON FILL
      {
        id: 'gl-draw-polygon-fill-active',
        type: 'fill',
        filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
        paint: {
          'fill-color': ['coalesce', ['get', 'user_color'], '#fbb03b'],
          'fill-opacity': 0.40,
        },
      },
      // INACTIVE POLYGON FILL
      {
        id: 'gl-draw-polygon-fill-inactive',
        type: 'fill',
        filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
        paint: {
          'fill-color': ['coalesce', ['get', 'user_color'], '#3bb2d0'],
          'fill-opacity': 0.40,
        },
      },
      // ACTIVE POLYGON STROKE - BLACK outline like original
      {
        id: 'gl-draw-polygon-stroke-active',
        type: 'line',
        filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': '#000000',
          'line-width': 2,
          'line-opacity': 0.95,
        },
      },
      // INACTIVE POLYGON STROKE - BLACK outline like original
      {
        id: 'gl-draw-polygon-stroke-inactive',
        type: 'line',
        filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': '#000000',
          'line-width': 1.5,
          'line-opacity': 0.95,
        },
      },
      // LINE STRING ACTIVE
      {
        id: 'gl-draw-line-active',
        type: 'line',
        filter: ['all', ['==', '$type', 'LineString'], ['==', 'active', 'true'], ['!=', 'mode', 'static']],
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': '#fbb03b',
          'line-dasharray': [0.2, 2],
          'line-width': 3,
        },
      },
      // LINE STRING INACTIVE
      {
        id: 'gl-draw-line-inactive',
        type: 'line',
        filter: ['all', ['==', '$type', 'LineString'], ['==', 'active', 'false'], ['!=', 'mode', 'static']],
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': '#3bb2d0',
          'line-width': 2,
        },
      },
      // VERTEX POINTS (corner handles) - shows when polygon is selected in direct_select
      {
        id: 'gl-draw-polygon-and-line-vertex-active',
        type: 'circle',
        filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
        paint: {
          'circle-radius': 6,
          'circle-color': '#ffffff',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fbb03b',
        },
      },
      // MIDPOINTS (for adding new vertices)
      {
        id: 'gl-draw-polygon-midpoint',
        type: 'circle',
        filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
        paint: {
          'circle-radius': 4,
          'circle-color': '#fbb03b',
        },
      },
      // POINT ACTIVE
      {
        id: 'gl-draw-point-active',
        type: 'circle',
        filter: ['all', ['==', '$type', 'Point'], ['!=', 'meta', 'midpoint'], ['!=', 'meta', 'vertex'], ['==', 'active', 'true']],
        paint: {
          'circle-radius': 7,
          'circle-color': '#fbb03b',
        },
      },
      // POINT INACTIVE
      {
        id: 'gl-draw-point-inactive',
        type: 'circle',
        filter: ['all', ['==', '$type', 'Point'], ['!=', 'meta', 'midpoint'], ['!=', 'meta', 'vertex'], ['==', 'active', 'false']],
        paint: {
          'circle-radius': 5,
          'circle-color': '#3bb2d0',
        },
      },
    ];

    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {},
      // Read-only share viewers get a view-only select mode: indications are
      // clickable (preview popup opens) but not draggable/deletable. Owners get
      // the normal editable 'simple_select'.
      defaultMode: isShareViewerRef.current
        ? (isFieldViewerRef.current ? 'field_select' : 'view_select')
        : 'simple_select',
      modes: { ...baseDrawModes, view_select: ViewSelectMode, field_select: FieldSelectMode },
      userProperties: true, // Enable user properties (stores ind_type, rotationDeg, etc.)
      styles: drawStyles,
    });

    map.addControl(draw);
    drawRef.current = draw;

    // Add navigation controls
    map.addControl(
      new mapboxgl.NavigationControl({
        visualizePitch: true,
      }),
      'bottom-right'
    );

    // Add scale control
    map.addControl(
      new mapboxgl.ScaleControl({
        maxWidth: 150,
        unit: 'metric',
      }),
      'bottom-left'
    );

    // Add attribution control
    map.addControl(
      new mapboxgl.AttributionControl({
        compact: true,
      }),
      'bottom-left'
    );

    // Track mouse position
    map.on('mousemove', (e) => {
      setCursorPosition(e.lngLat.lng, e.lngLat.lat);
    });

    map.on('mouseout', () => {
      setCursorPosition(null, null);
    });

    // Track view changes
    map.on('moveend', () => {
      const mapCenter = map.getCenter();
      setView({
        center: [mapCenter.lng, mapCenter.lat],
        zoom: map.getZoom(),
        bearing: map.getBearing(),
        pitch: map.getPitch(),
      });
    });

    // Map ready
    map.on('load', () => {
      mapRef.current = map;
      setMap(map);
      setDraw(draw);

      // Add sources for other layers (not indications - those use draw)
      map.addSource('flight-paths', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      map.addSource('geo-photos', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      map.addSource('data-points', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      map.addSource('user-location', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      // Add flight paths layer
      map.addLayer({
        id: 'flight-paths-line',
        type: 'line',
        source: 'flight-paths',
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 2,
          'line-dasharray': [2, 2],
        },
      });

      // Flight path start/end directional triangle markers
      map.addImage('flight-arrow-start', createTriangleImage('#27AE60', 20), { sdf: false });
      map.addImage('flight-arrow-end', createTriangleImage('#EB5757', 20), { sdf: false });

      map.addSource('flight-path-endpoints', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      // Start marker (green triangle pointing in flight direction)
      map.addLayer({
        id: 'flight-path-start',
        type: 'symbol',
        source: 'flight-path-endpoints',
        filter: ['==', ['get', 'role'], 'start'],
        layout: {
          'icon-image': 'flight-arrow-start',
          'icon-size': 1,
          'icon-rotate': ['get', 'bearing'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });

      // End marker (red triangle pointing in flight direction)
      map.addLayer({
        id: 'flight-path-end',
        type: 'symbol',
        source: 'flight-path-endpoints',
        filter: ['==', ['get', 'role'], 'end'],
        layout: {
          'icon-image': 'flight-arrow-end',
          'icon-size': 1,
          'icon-rotate': ['get', 'bearing'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });

      // Add geo photos layer
      map.addLayer({
        id: 'geo-photos-point',
        type: 'circle',
        source: 'geo-photos',
        paint: {
          'circle-radius': 6,
          'circle-color': '#3b82f6',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });

      // Add data points layer (hidden — HTML markers handle rendering + drag)
      map.addLayer({
        id: 'data-points-marker',
        type: 'circle',
        source: 'data-points',
        layout: { visibility: 'none' },
        paint: {
          'circle-radius': 10,
          'circle-color': ['get', 'color'],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });

      // Guide line (field navigation): one source holds the user→target
      // LineString + the target Point; layers filter by geometry type.
      // White casing under a dashed blue line so it reads over both dark
      // thermal orthos and bright RGB.
      map.addSource('guide', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      map.addLayer({
        id: 'guide-line-casing',
        type: 'line',
        source: 'guide',
        filter: ['==', ['geometry-type'], 'LineString'],
        layout: { 'line-cap': 'round' },
        paint: {
          'line-color': '#ffffff',
          'line-width': 7,
          'line-opacity': 0.7,
        },
      });

      map.addLayer({
        id: 'guide-line',
        type: 'line',
        source: 'guide',
        filter: ['==', ['geometry-type'], 'LineString'],
        paint: {
          'line-color': '#2563eb',
          'line-width': 3,
          'line-dasharray': [1.5, 1.5],
        },
      });

      map.addLayer({
        id: 'guide-target-point',
        type: 'circle',
        source: 'guide',
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': 7,
          'circle-color': '#2563eb',
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff',
        },
      });

      // Add user location layers. Accuracy radius starts at 0 — the location
      // effect sets a zoom-interpolated expression per fix so the circle stays
      // ground-true at every zoom (not just the zoom of the last GPS update).
      map.addLayer({
        id: 'user-location-accuracy',
        type: 'circle',
        source: 'user-location',
        paint: {
          'circle-radius': 0,
          'circle-color': '#3b82f6',
          'circle-opacity': 0.2,
        },
      });

      // GPS course cone — only rendered while actually moving (hasHeading).
      map.addImage('location-heading-cone', createHeadingConeImage(128), { sdf: false });
      map.addLayer({
        id: 'user-location-heading',
        type: 'symbol',
        source: 'user-location',
        filter: ['==', ['get', 'hasHeading'], true],
        layout: {
          'icon-image': 'location-heading-cone',
          'icon-size': 1,
          'icon-rotate': ['get', 'heading'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });

      map.addLayer({
        id: 'user-location-dot',
        type: 'circle',
        source: 'user-location',
        paint: {
          'circle-radius': 8,
          'circle-color': '#3b82f6',
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff',
        },
      });

      // Build B: ✓ badge over RESOLVED faults (Current mode only). The fault keeps
      // its type color via the draw fill; this symbol layer overlays a green ✓ at
      // its centroid. Data is driven by syncIndicationsToDraw (empty in As-found
      // mode / when Show-resolved is off), so the layer itself is always present.
      map.addSource('resolved-badges', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      if (!map.hasImage('resolved-check')) {
        map.addImage('resolved-check', createCheckImage(48), { sdf: false });
      }
      map.addLayer({
        id: 'resolved-badges',
        type: 'symbol',
        source: 'resolved-badges',
        layout: {
          'icon-image': 'resolved-check',
          // Scale with zoom to track the GEOGRAPHIC fault marker, which grows ~2× per
          // zoom level. Base 2 + a ~64× range over zooms 16→22 makes the ✓ shrink
          // to a dot when zoomed out and grow with the marker when zoomed in, instead
          // of a fixed pixel size. Tunable stops.
          'icon-size': ['interpolate', ['exponential', 2], ['zoom'], 16, 0.025, 22, 1.6],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });

      // P2a fault detection: ghost candidate overlay. One source carries the
      // candidate POLYGONS plus centroid POINTS for accepted-✓ badges; the
      // layers filter by geometry type + `state`. promoteId keys feature-state
      // hover off the string candId. Styling is deliberately distinct from real
      // indications (dashed violet outline + faint fill vs. solid black outline
      // + 0.40 type-color fill). Accepted candidates restyle to a SOLID green
      // outline + ✓ instead of disappearing, so below the indication zoom gate
      // (z16, where the new real indication is hidden) an accept never reads as
      // a delete. NOT zoom-gated for the same reason. Data flows in via the
      // detection sync effect (admin/operator non-share sessions only).
      map.addSource('detection-candidates', {
        type: 'geojson',
        promoteId: 'candId',
        data: { type: 'FeatureCollection', features: [] },
      });

      map.addLayer({
        id: 'detection-ghost-fill',
        type: 'fill',
        source: 'detection-candidates',
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: {
          'fill-color': [
            'case',
            ['==', ['get', 'state'], 'accepted'],
            '#16a34a',
            '#a855f7',
          ],
          'fill-opacity': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            0.3,
            ['case', ['==', ['get', 'state'], 'accepted'], 0.08, 0.15],
          ],
        },
      });

      map.addLayer({
        id: 'detection-ghost-outline',
        type: 'line',
        source: 'detection-candidates',
        filter: ['all', ['==', ['geometry-type'], 'Polygon'], ['==', ['get', 'state'], 'ghost']],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#a855f7',
          'line-width': 2,
          'line-dasharray': [2, 2],
          'line-opacity': 0.95,
        },
      });

      map.addLayer({
        id: 'detection-accepted-outline',
        type: 'line',
        source: 'detection-candidates',
        filter: ['all', ['==', ['geometry-type'], 'Polygon'], ['==', ['get', 'state'], 'accepted']],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': '#16a34a',
          'line-width': 2.5,
          'line-opacity': 0.95,
        },
      });

      // Reuses the 'resolved-check' image added above. Floor stop keeps the ✓
      // legible below the indication zoom gate (ghosts are not zoom-gated).
      map.addLayer({
        id: 'detection-accepted-check',
        type: 'symbol',
        source: 'detection-candidates',
        filter: ['all', ['==', ['geometry-type'], 'Point'], ['==', ['get', 'state'], 'accepted']],
        layout: {
          'icon-image': 'resolved-check',
          'icon-size': ['interpolate', ['exponential', 2], ['zoom'], 14, 0.18, 22, 1.2],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });

      // Signal map ready AFTER all sources & layers exist so dependent
      // useEffects can safely access them on first run.
      setMapReady(true);

      // Initial sync of indications
      syncIndicationsToDrawRef.current?.();
    });

    // Handle draw events - sync changes back to store
    map.on('draw.update', (e: { features: GeoJSON.Feature[] }) => {
      syncingFromStoreRef.current = true;
      e.features.forEach((feature) => {
        const id = feature.id as string;
        if (feature.geometry.type === 'Polygon') {
          if (isShareViewerRef.current) {
            // FL-2.5: field visitors persist through the ownership-gated
            // field path (server 403s non-own); view visitors can't reach
            // draw.update at all (ViewSelectMode no-ops every edit gesture).
            // Topology is recomputed here and rides the same PATCH — the
            // operator path below (reapplyTopologyForIndication →
            // updateIndication) isn't reachable for a field session.
            useProjectStore.getState().setIndicationFieldGeometry(
              id,
              feature.geometry,
              computeTopologyUpdates(id, feature.geometry) ?? undefined,
            );
          } else {
            useProjectStore.getState().updateIndication(id, {
              geometry: feature.geometry,
            });
            // Re-evaluate topology fields for the new location
            reapplyTopologyForIndication(id);
          }
        }
      });
      syncingFromStoreRef.current = false;
    });

    // Handle draw delete - sync deletions back to project store
    map.on('draw.delete', (e: { features: GeoJSON.Feature[] }) => {
      e.features.forEach((feature) => {
        const id = feature.id as string;
        useProjectStore.getState().removeIndication(id);
      });
    });

    // Handle draw selection with Ctrl+click multi-select support
    map.on('draw.selectionchange', (e: { features: GeoJSON.Feature[] }) => {
      // While a lasso is being drawn/finalized, Draw emits transitional
      // selection events (incl. selecting the just-completed polygon) that
      // must not touch the store — the deferred draw.create block owns it.
      if (useMapStore.getState().areaSelectMode || lassoFinishingRef.current) return;
      // Native box select (Shift+drag in simple_select): Draw selected several
      // features at once — mirror ALL of them into the store (previously this
      // collapsed to features[0], leaving the store blind to the rest).
      if (e.features.length > 1) {
        useMapStore.getState().setSelectedFeatureIds(e.features.map((f) => String(f.id)));
        useMapStore.getState().setPreviewIndication(null);
        return;
      }
      if (e.features.length > 0) {
        const feature = e.features[0];
        const featureId = feature.id as string;

        // Batch status paint mode: a plain click applies the target status —
        // no preview, mode stays armed. Ctrl+click falls through to normal
        // multi-select. Read state at event time (handler registered once).
        const ui = useUIStore.getState();
        if (ui.batchStatusMode && !ctrlKeyRef.current) {
          // Drop draw's selection immediately so the marker can't be drag-moved
          // and can be re-clicked later. This re-fires selectionchange with []
          // — that branch only clears store selection/preview, which is what
          // we want while painting.
          drawRef.current?.changeMode('simple_select', { featureIds: [] });
          const target = ui.batchStatusTarget;
          const ind = useProjectStore.getState().data.indications.find((i) => i.id === featureId);
          if (!ind) return;
          if ((ind.properties.status ?? 'open') === target) {
            ui.addNotification({
              type: 'info',
              title: `Already ${STATUS_LABELS[target]}`,
              duration: 1500,
            });
            return;
          }
          useProjectStore.getState().setIndicationStatus(featureId, target).then((result) => {
            if (result === 'error') {
              useUIStore.getState().addNotification({
                type: 'error',
                title: 'Status change failed',
                message: 'Change reverted — check connection and retry',
              });
            } else if (result === 'conflict') {
              useUIStore.getState().addNotification({
                type: 'warning',
                title: 'Edited elsewhere',
                message: 'Reloaded latest state — click again if still needed',
              });
            }
          });
          // Optimistic short toast: setIndicationStatus flips the status
          // synchronously; failures surface via the toasts above.
          ui.addNotification({
            type: 'success',
            title: `→ ${STATUS_LABELS[target]}`,
            duration: 1500,
          });
          return;
        }

        // If Ctrl is held, toggle selection (add/remove from multi-select)
        if (ctrlKeyRef.current) {
          toggleSelection(featureId);
          // Mirror the store's multi-set back into Draw so EVERY selected
          // marker renders in the active (highlighted) style — Draw alone
          // would only highlight the last-clicked feature. Programmatic
          // changeMode fires no draw events, so this can't loop.
          const sel = useMapStore.getState().selectedFeatureIds;
          if (drawRef.current?.getMode() === 'simple_select') {
            drawRef.current.changeMode('simple_select', { featureIds: sel });
          }
        } else {
          // Normal click - select only this feature
          selectFeature(featureId);
        }

        // If single indication selected, open floating info preview (not full modal)
        if (e.features.length === 1 && !ctrlKeyRef.current) {
          useMapStore.getState().setPreviewIndication(featureId);
        }
      } else {
        // Clicked on empty space - clear selection and preview (unless Ctrl held)
        if (!ctrlKeyRef.current) {
          clearSelection();
          useMapStore.getState().setPreviewIndication(null);
        }
      }
    });

    // Area select: while areaSelectMode is armed, the drawn polygon is a
    // selection lasso, never an indication — select every visible indication
    // whose centroid falls inside it, then discard the polygon itself.
    map.on('draw.create', (e: { features: GeoJSON.Feature[] }) => {
      if (!useMapStore.getState().areaSelectMode) return;
      const lasso = e.features[0];
      if (!lasso || lasso.geometry.type !== 'Polygon') {
        if (lasso) drawRef.current?.delete(String(lasso.id));
        useMapStore.getState().setAreaSelectMode(false);
        return;
      }
      lassoFinishingRef.current = true;
      const lassoId = String(lasso.id);
      const ring = (lasso.geometry.coordinates[0] as [number, number][]).slice(0, -1);
      // Compute hits synchronously (feature set is stable right now)…
      const hits: string[] = [];
      for (const f of (drawRef.current?.getAll().features ?? [])) {
        const fid = String(f.id);
        if (fid === lassoId || f.geometry.type !== 'Polygon') continue;
        const center = centroidOfPolygon(f.geometry.coordinates[0] as number[][]);
        if (center && pointInPolygon(center, ring)) hits.push(fid);
      }
      // …but finalize on the NEXT tick: right after draw.create, Draw itself
      // transitions to simple_select with the new polygon selected, emitting a
      // selectionchange that would land after (and clobber) anything we set
      // here. areaSelectMode stays true until the deferred block runs, and the
      // selectionchange handler ignores events while it's true.
      setTimeout(() => {
        lassoFinishingRef.current = false;
        const d = drawRef.current;
        if (!d) return;
        d.delete(lassoId);
        const st = useMapStore.getState();
        st.setSelectedFeatureIds(hits);
        st.setPreviewIndication(null);
        if (hits.length > 0) d.changeMode('simple_select', { featureIds: hits });
        st.setAreaSelectMode(false);
        useUIStore.getState().addNotification({
          type: hits.length ? 'success' : 'info',
          title: hits.length
            ? `${hits.length} indication${hits.length === 1 ? '' : 's'} selected`
            : 'No indications inside the drawn area',
          duration: 2500,
        });
      }, 0);
    });

    // Close preview when entering direct_select mode (geometry editing).
    // Also disarm area-select if the user cancels the lasso (Esc exits
    // draw_polygon back to simple_select without a draw.create).
    map.on('draw.modechange', (e: { mode: string }) => {
      // F-02E hardening: a share viewer must never reach an editable stock
      // mode. View-kind visitors are pinned to view_select. FL-2.5: FIELD
      // visitors live in field_select and may enter direct_select (vertex
      // editing — FieldSelectMode only opens that door for own-created
      // features); anything else is reverted on the next tick.
      // Programmatic draw.changeMode() fires no events, so this cannot loop.
      if (isShareViewerRef.current) {
        const allowed = isFieldViewerRef.current
          ? e.mode === 'field_select' || e.mode === 'direct_select'
          : e.mode === 'view_select';
        if (!allowed) {
          setTimeout(() => {
            if (!isShareViewerRef.current) return;
            drawRef.current?.changeMode(
              isFieldViewerRef.current ? 'field_select' : 'view_select'
            );
          }, 0);
          return;
        }
      }
      if (e.mode === 'direct_select') {
        useMapStore.getState().setPreviewIndication(null);
      }
      // Esc-cancel disarms; polygon COMPLETION also passes through here, but
      // then lassoFinishingRef is set and the deferred create block disarms.
      if (e.mode !== 'draw_polygon' && useMapStore.getState().areaSelectMode && !lassoFinishingRef.current) {
        useMapStore.getState().setAreaSelectMode(false);
      }
    });

    // Stale-selection reconcile: syncIndicationsToDraw updates a changed
    // feature via delete+add, which silently drops it from Draw's internal
    // selection WITHOUT firing selectionchange. The store then holds phantom
    // selectedFeatureIds that neither Escape nor empty-map clicks can clear —
    // Draw's selection is already empty, so no [] event ever fires — while
    // "Apply to selected" still targets them invisibly. On any plain click,
    // if Draw has no selection but the store does, clear the store. Runs after
    // Draw's own click handling (Draw registered first), so a feature click has
    // already re-populated Draw's selection by the time this checks it.
    map.on('click', () => {
      if (ctrlKeyRef.current) return;
      if (useMapStore.getState().areaSelectMode || lassoFinishingRef.current) return;
      const drawSelected = drawRef.current?.getSelectedIds() ?? [];
      if (drawSelected.length > 0) return;
      const st = useMapStore.getState();
      if (st.selectedFeatureIds.length > 0 || st.selectedFeatureId) {
        clearSelection();
        st.setPreviewIndication(null);
      }
    });

    // Recover from WebGL context loss (e.g. GPU pressure in split-screen anchor mode)
    const canvas = map.getCanvas();
    let contextRestoreTimer: ReturnType<typeof setTimeout> | null = null;
    const handleContextLost = (e: Event) => {
      e.preventDefault(); // tells the browser we'll handle recovery
      console.warn('[MapCanvas] WebGL context lost — will restore');
      // Cancel any pending restore to avoid thrashing
      if (contextRestoreTimer) { clearTimeout(contextRestoreTimer); contextRestoreTimer = null; }
    };
    const handleContextRestored = () => {
      // Delay repaint to let GPU settle after context restore
      contextRestoreTimer = setTimeout(() => {
        contextRestoreTimer = null;
        if (map && !(map as any)._removed) {
          console.log('[MapCanvas] WebGL context restored — triggering repaint');
          map.triggerRepaint();
        }
      }, 500);
    };
    canvas.addEventListener('webglcontextlost', handleContextLost);
    canvas.addEventListener('webglcontextrestored', handleContextRestored);

    // Handle container resize
    const resizeObserver = new ResizeObserver(() => {
      map.resize();
    });

    if (mapContainerRef.current) {
      resizeObserver.observe(mapContainerRef.current);
    }

    // Cleanup
    return () => {
      canvas.removeEventListener('webglcontextlost', handleContextLost);
      canvas.removeEventListener('webglcontextrestored', handleContextRestored);
      resizeObserver.disconnect();
      // The field long-press hold lives at module scope (the draw mode has no
      // instance to hang it on) — gl-draw's control removal never calls
      // mode.onStop, so drop the timer and its listener here or they outlive
      // this map and fire into a torn-down draw.
      endFieldHold();
      map.remove();
      mapRef.current = null;
      drawRef.current = null;
      setMap(null);
      setDraw(null);
      setMapReady(false);
    };
  }, []);

  // Manual orthophoto positioning markers (corners, center, rotation handle)
  useManualOrthoMarkers();

  // Overlay positioning markers (corners, sides, center handle)
  useOverlayMarkers();

  // On-map overlay crop / delete area drawing
  useOverlayEdit();

  // Batch move geo photos (drag all photos as a group)
  useBatchMove();
  useBatchDelete();

  // Layout overlay (raster image + text labels from vector PDF)
  // Share viewers never see the vector-layout surface: passing `enabled=false`
  // keeps the hook's effect from rendering label/grid/image layers (and its
  // cleanup removes them if viewer status settles after map init).
  // Layout surface (module grid, string lines, labels): editor always; a
  // share viewer only when the link exposes the 'layout' section (owner,
  // 2026-09-27), and then strictly non-interactive — no label/grid drag
  // handlers are registered for viewers.
  const layoutAllowed = useShareSectionAllowed('layout');
  useLayoutOverlay(mapRef, isMapReady && (!isShareViewer || layoutAllowed), !isShareViewer);

  // Plan vector layers (rung 3 / R3-D: companion DXF or PDF extraction).
  // Rendered for share viewers too — owner ruling 2026-09-27: always on
  // whenever the operator picked layers; the picks are the operator's, so a
  // visitor sees exactly the chosen layers, and the legend's Vectors checkbox
  // is their own show/hide. (The layout surface below stays editor-only.)
  useDxfLayers(mapRef, isMapReady);

  // Macro adjust for those vectors (drag / arrow-key nudge) — editor only.
  useDxfAdjust(mapRef, isMapReady && !isShareViewer);

  // Local as-built corrections on those vectors (draw a zone, nudge it).
  useDxfZones(mapRef, isMapReady && !isShareViewer);

  // IV result circles on assigned string positions
  useIVOverlay(mapRef, isMapReady);

  // Data point markers (monitoring builder)
  const [dpPopup, setDpPopup] = useState<{ dpId: string; position: { x: number; y: number } } | null>(null);
  useDataPointMarkers(mapRef, isMapReady, (dpId, position) => {
    setDpPopup({ dpId, position });
  });

  // Data point placement mode — driven by uiStore.setAddMode(true, 'dataPoint')
  const [isDataPointPlacing, setIsDataPointPlacing] = useState(false);
  const dpAddMode = useUIStore((s) => s.isAddMode && s.addModeType === 'dataPoint');

  useEffect(() => {
    setIsDataPointPlacing(dpAddMode);
  }, [dpAddMode]);

  useDataPointPlacement(mapRef, isMapReady, isDataPointPlacing, {
    onPlace: (coordinates) => {
      setIsDataPointPlacing(false);
      useUIStore.getState().setAddMode(false);
      useUIStore.getState().openModal('editDataPoint', {
        isNew: true,
        coordinates,
      });
    },
    onCancel: () => {
      setIsDataPointPlacing(false);
      useUIStore.getState().setAddMode(false);
    },
  });

  // Flight path popup state
  const [fpPopup, setFpPopup] = useState<{ id: string; lngLat: mapboxgl.LngLat } | null>(null);

  // ── P2a fault detection: ghost candidate overlay ──
  // Visible ONLY for admin/operator non-share sessions — same predicate as
  // DetectionPanel.canScan (UI mirror of the backend isAuthorized(['admin',
  // 'operator']) gate: platform-admin OR org owner/admin/operator).
  // Ghost popup id lives in mapStore (detectionPopupCandId) so BOTH the map
  // click path here and the Detection ticket's ranked list can open it.
  const ghostPopupCandId = useMapStore((s) => s.detectionPopupCandId);
  const setDetectionPopupCand = useMapStore((s) => s.setDetectionPopupCand);
  const authUser = useAuthStore((s) => s.user);
  const currentOrg = useAuthStore((s) => s.currentOrg);
  const detectionCandidates = useDetectionStore((s) => s.candidates);
  const detectionThreshold = useDetectionStore((s) => s.threshold);
  const canReviewGhosts =
    !isShareViewer &&
    (authUser?.role === 'admin' ||
      currentOrg?.role === 'owner' ||
      currentOrg?.role === 'admin' ||
      currentOrg?.role === 'operator');

  // Get indication filters and layer visibility for reactive updates
  const indicationFilters = useMapStore((state) => state.indicationFilters);
  const indicationShowResolved = useMapStore((state) => state.indicationShowResolved);
  const indicationFieldLens = useMapStore((state) => state.indicationFieldLens);
  const indicationViewMode = useMapStore((state) => state.indicationViewMode);
  const layerVisibility = useMapStore((state) => state.layerVisibility);
  const visibleDates = useProjectStore((s) => s.visibleDates);

  // Sync indications to draw when data or filters change
  useEffect(() => {
    if (!isMapReady || !drawRef.current) return;
    syncIndicationsToDrawRef.current?.();
  }, [indications, isMapReady, indicationFilters, visibleDates, indicationShowResolved, indicationFieldLens, indicationViewMode]);

  // ── Zoom-dependent declutter ──
  // Zoom thresholds: >= 16 everything, 14–16 dots only, < 14 minimal
  const ZOOM_INDICATIONS = 16;   // indications (draw layers) visible at >= 16
  const ZOOM_FLIGHT_GEO = 14;    // flight paths + geo photos visible at >= 14
  const zoomRef = useRef(15);

  // Track zoom and toggle layer visibility based on zoom thresholds
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;

    const DRAW_LAYER_IDS = [
      'gl-draw-polygon-fill-active', 'gl-draw-polygon-fill-inactive',
      'gl-draw-polygon-stroke-active', 'gl-draw-polygon-stroke-inactive',
      'gl-draw-line-active', 'gl-draw-line-inactive',
      'gl-draw-polygon-and-line-vertex-active', 'gl-draw-polygon-midpoint',
      'gl-draw-point-active', 'gl-draw-point-inactive',
    ];

    function applyZoomVisibility() {
      const zoom = map!.getZoom();
      zoomRef.current = zoom;
      const lv = useMapStore.getState().layerVisibility;

      // Indications (MapboxDraw layers) — visible at zoom >= 16
      const drawVis = (zoom >= ZOOM_INDICATIONS && lv.indications) ? 'visible' : 'none';
      for (const id of DRAW_LAYER_IDS) {
        if (map!.getLayer(id)) {
          map!.setLayoutProperty(id, 'visibility', drawVis);
        }
      }
      // ✓ badges track indication visibility (zoom threshold + layer toggle).
      if (map!.getLayer('resolved-badges')) map!.setLayoutProperty('resolved-badges', 'visibility', drawVis);

      // Flight paths — visible at zoom >= 14
      const fpVis = (zoom >= ZOOM_FLIGHT_GEO && lv.flightPaths) ? 'visible' : 'none';
      if (map!.getLayer('flight-paths-line')) map!.setLayoutProperty('flight-paths-line', 'visibility', fpVis);
      if (map!.getLayer('flight-path-start')) map!.setLayoutProperty('flight-path-start', 'visibility', fpVis);
      if (map!.getLayer('flight-path-end')) map!.setLayoutProperty('flight-path-end', 'visibility', fpVis);

      // Geo photos — visible at zoom >= 14
      const gpVis = (zoom >= ZOOM_FLIGHT_GEO && lv.geoPhotos) ? 'visible' : 'none';
      if (map!.getLayer('geo-photos-point')) map!.setLayoutProperty('geo-photos-point', 'visibility', gpVis);
    }

    applyZoomVisibility();
    map.on('zoom', applyZoomVisibility);
    return () => { map.off('zoom', applyZoomVisibility); };
  }, [isMapReady]);

  // Toggle flight paths layer visibility (respects zoom threshold)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;
    const vis = (layerVisibility.flightPaths && zoomRef.current >= ZOOM_FLIGHT_GEO) ? 'visible' : 'none';
    if (map.getLayer('flight-paths-line')) {
      map.setLayoutProperty('flight-paths-line', 'visibility', vis);
    }
    if (map.getLayer('flight-path-start')) {
      map.setLayoutProperty('flight-path-start', 'visibility', vis);
    }
    if (map.getLayer('flight-path-end')) {
      map.setLayoutProperty('flight-path-end', 'visibility', vis);
    }
  }, [layerVisibility.flightPaths, isMapReady]);

  // Toggle geo photos layer visibility (respects zoom threshold)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;
    if (map.getLayer('geo-photos-point')) {
      map.setLayoutProperty('geo-photos-point', 'visibility',
        (layerVisibility.geoPhotos && zoomRef.current >= ZOOM_FLIGHT_GEO) ? 'visible' : 'none');
    }
  }, [layerVisibility.geoPhotos, isMapReady]);

  // Toggle data points visibility — HTML markers are toggled by useDataPointMarkers
  // via subscribe(). The circle layer stays permanently hidden (used only for z-ordering).
  // No need to toggle 'data-points-marker' here; doing so would duplicate the dots.

  // Update flight paths on map
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;

    const source = map.getSource('flight-paths') as mapboxgl.GeoJSONSource;
    if (!source) return;

    // Date filter for flight paths
    const fpDatesSet = new Set(useProjectStore.getState().visibleDates);
    const fpHasDates = useProjectStore.getState().availableDates.length > 0;

    const features = flightPaths
      .filter((fp) => {
        if (!fp.visible) return false;
        if (fpHasDates && fp.captureDate && !fpDatesSet.has(captureDay(fp.captureDate))) return false;
        return true;
      })
      .map((flightPath) => ({
        type: 'Feature' as const,
        id: flightPath.id,
        geometry: {
          type: 'LineString' as const,
          coordinates: flightPath.coordinates.map((coord) =>
            coord[2] !== undefined ? [coord[0], coord[1], coord[2]] : [coord[0], coord[1]]
          ) as [number, number][] | [number, number, number][],
        },
        properties: {
          id: flightPath.id,
          name: flightPath.name,
          color: flightPath.color,
        },
      }));

    source.setData({
      type: 'FeatureCollection',
      features,
    });

    // Update start/end directional triangle markers
    const epSource = map.getSource('flight-path-endpoints') as mapboxgl.GeoJSONSource;
    if (epSource) {
      const endpoints: GeoJSON.Feature[] = [];
      for (const fp of flightPaths) {
        if (!fp.visible || fp.coordinates.length < 2) continue;
        if (fpHasDates && fp.captureDate && !fpDatesSet.has(captureDay(fp.captureDate))) continue;
        const coords = fp.coordinates;
        const first = coords[0];
        const last = coords[coords.length - 1];

        // Use a point further along the path for stable bearing (skip GPS jitter).
        // Look ahead ~30 points (~1 sec at 30fps) or 10% of the path, whichever is larger.
        const skipStart = Math.min(Math.max(30, Math.floor(coords.length * 0.1)), coords.length - 1);
        const skipEnd = Math.max(0, coords.length - 1 - skipStart);
        const startTarget = coords[skipStart];
        const endSource = coords[skipEnd];

        const startBearing = computeBearing([first[0], first[1]], [startTarget[0], startTarget[1]]);
        endpoints.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [first[0], first[1]] },
          properties: { role: 'start', parentId: fp.id, name: fp.name, color: fp.color, bearing: startBearing },
        });

        const endBearing = computeBearing([endSource[0], endSource[1]], [last[0], last[1]]);
        endpoints.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [last[0], last[1]] },
          properties: { role: 'end', parentId: fp.id, name: fp.name, color: fp.color, bearing: endBearing },
        });
      }
      epSource.setData({ type: 'FeatureCollection', features: endpoints });
    }

    // Ensure flight paths stay above raster layers (overlays/orthophotos)
    ensureLayerOrder(map);
  }, [flightPaths, isMapReady, visibleDates]);

  // Update geo photos on map
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;

    const source = map.getSource('geo-photos') as mapboxgl.GeoJSONSource;
    if (!source) return;

    const features = geoPhotos.map((photo) => ({
      type: 'Feature' as const,
      id: photo.id,
      geometry: {
        type: 'Point' as const,
        coordinates: photo.coordinates,
      },
      properties: {
        id: photo.id,
        url: photo.url,
        bearing: photo.bearing,
      },
    }));

    source.setData({
      type: 'FeatureCollection',
      features,
    });
  }, [geoPhotos, isMapReady]);

  // Update PNG/PDF overlays on map — INCREMENTAL updates to avoid WebGL context loss.
  // Only recreates source when URL changes. For coordinates/opacity/visibility, updates in-place.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;

    let cancelled = false;

    (async () => {
      // Pre-resolve signed URLs for any image-source overlays that may hit the
      // (re)create path this pass. Mapbox image sources go through Image() with
      // crossOrigin='anonymous' which strips cookies, so /api/v2/assets/ URLs
      // need ?expires=&sig= to load. Tile-mode overlays skip this — except the
      // selected one, which renders as an image while its edit handles show.
      // This predicate MUST stay identical to `useTiles` below: anything that
      // ends up on the image path needs a signed URL, and a plan whose bake no
      // longer matches its placement takes that path even though it has ready
      // tiles. Filtering on tileStatus alone left such a plan unsigned, so its
      // /api/v2/assets URL 401'd under crossOrigin='anonymous' and the layer
      // vanished from the map completely — no tiles AND no proxy.
      const urlsNeedingResolution = overlays
        .filter((o) => o.visible && (!canRenderBakedTiles(o) || o.id === adjustingOverlayId))
        .map((o) => o.url);
      const signedMap = urlsNeedingResolution.length
        ? await resolveSignedUrls(urlsNeedingResolution)
        : new Map<string, string>();

      if (cancelled || mapRef.current !== map) return;

      const currentIds = new Set(overlays.map((o) => o.id));
      const previousIds = renderedOverlaysRef.current;

      // Remove overlays that are no longer in data
      previousIds.forEach((id) => {
        if (!currentIds.has(id)) {
          const sourceId = `overlay-${id}`;
          const layerId = `overlay-layer-${id}`;
          try { if (map.getLayer(layerId)) map.removeLayer(layerId); } catch { /* context lost */ }
          try { if (map.getSource(sourceId)) map.removeSource(sourceId); } catch { /* context lost */ }
          previousIds.delete(id);
          overlayUrlsRef.current.delete(id);
        }
      });

      // Update or add overlays (overlays are NOT date-filtered — always visible when layer checkbox is on)
      overlays.forEach((overlay) => {
        const sourceId = `overlay-${overlay.id}`;
        const layerId = `overlay-layer-${overlay.id}`;
        const existingSource = map.getSource(sourceId) as mapboxgl.ImageSource | undefined;
        const previousUrl = overlayUrlsRef.current.get(overlay.id);

        // Determine render mode: raster tiles (full res) vs image source
        // (downsampled preview). The selected overlay renders as an image so
        // its edit handles can move it live (tile sources can't follow drags).
        //
        // canRenderBakedTiles additionally vetoes tiles for a re-registered
        // plan: baked tiles were warped to the corners the bake ran against, so
        // after the plan is moved they would be crisp but in the WRONG PLACE.
        // The proxy image re-corners instantly, so it takes over until re-bake.
        const useTiles = canRenderBakedTiles(overlay) && overlay.id !== adjustingOverlayId;
        const trackingKey = overlay.url + (useTiles ? `#tile:${overlay.tilesetId}` : '');
        const prevMode = tileSourceModeRef.current.get(`overlay-${overlay.id}`);
        const modeChanged = useTiles ? prevMode !== 'tile' : prevMode === 'tile';

        if (existingSource && previousUrl === trackingKey && !modeChanged) {
          // Source exists with same URL and mode — update in-place (no GPU churn)
          if (!useTiles) {
            try {
              (existingSource as any).setCoordinates(overlay.bounds);
            } catch (_e) { /* ignore */ }
          }

          if (map.getLayer(layerId)) {
            try {
              // Must go through the helper like the create path below: this
              // runs on EVERY effect pass (any overlays change, every slider
              // tick), so writing a plain number here would quietly undo the
              // zoom fade the moment the user touched anything.
              map.setPaintProperty(layerId, 'raster-opacity', overlayRasterOpacity(overlay) as any);
              map.setLayoutProperty(layerId, 'visibility', overlay.visible ? 'visible' : 'none');
            } catch (_e) { /* ignore */ }
          }
        } else {
          // New source, URL changed, or mode changed — (re)create
          try { if (map.getLayer(layerId)) map.removeLayer(layerId); } catch { /* context lost */ }
          try { if (map.getSource(sourceId)) map.removeSource(sourceId); } catch { /* context lost */ }

          if (!overlay.visible) {
            previousIds.delete(overlay.id);
            overlayUrlsRef.current.delete(overlay.id);
            return;
          }

          if (useTiles) {
            // Full-resolution raster tile source
            const tileUrl = buildTileUrl(overlay.tilesetId!);
            // Compute axis-aligned bounds from 4-corner coordinates
            const lngs = overlay.bounds.map((c: [number, number]) => c[0]);
            const lats = overlay.bounds.map((c: [number, number]) => c[1]);
            map.addSource(sourceId, {
              type: 'raster',
              tiles: [tileUrl],
              tileSize: 512,
              bounds: [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)] as [number, number, number, number],
              minzoom: overlay.tileMinZoom ?? 10,
              maxzoom: overlay.tileMaxZoom ?? 22,
            });
          } else {
            // Downsampled image source (immediate preview or small images).
            // Use signed URL when available (raw /api/v2/assets/... URLs 401
            // under crossOrigin='anonymous'); blob: and tile URLs pass through.
            const imageUrl = signedMap.get(overlay.url) ?? overlay.url;
            map.addSource(sourceId, {
              type: 'image',
              url: imageUrl,
              coordinates: overlay.bounds,
            });
          }

          map.addLayer({
            id: layerId,
            type: 'raster',
            source: sourceId,
            paint: {
              'raster-opacity': overlayRasterOpacity(overlay) as any,
              'raster-fade-duration': 0,
            },
            layout: {
              visibility: overlay.visible ? 'visible' : 'none',
            },
          });

          // Keep draw (indication) layers on top of newly added overlay
          ensureLayerOrder(map);

          overlayUrlsRef.current.set(overlay.id, trackingKey);
          tileSourceModeRef.current.set(`overlay-${overlay.id}`, useTiles ? 'tile' : 'image');
          previousIds.add(overlay.id);
        }
      });
    })();

    return () => { cancelled = true; };
  }, [overlays, isMapReady, adjustingOverlayId]);

  // Update orthophoto layers on map — INCREMENTAL updates to avoid WebGL context loss.
  // Only recreates source when URL changes. For coordinates/opacity/visibility, updates in-place.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;

    let cancelled = false;

    (async () => {
      // Pre-resolve signed URLs for orthos that may hit the image-source
      // (re)create path. Tile-mode orthos pass through — except the one under
      // adjustment, which renders as an image; see overlay effect above.
      const urlsNeedingResolution = orthophotos
        .filter((o) => o.visible && (!(o.tileStatus === 'ready' && !!o.tilesetId) || o.id === editingOrthoId))
        .map((o) => o.url);
      const signedMap = urlsNeedingResolution.length
        ? await resolveSignedUrls(urlsNeedingResolution)
        : new Map<string, string>();

      if (cancelled || mapRef.current !== map) return;

      const currentIds = new Set(orthophotos.map((o) => o.id));
      const previousIds = renderedOrthosRef.current;

      // Remove orthophotos that are no longer in data
      const toRemove = Array.from(previousIds).filter((id) => !currentIds.has(id));
      for (const id of toRemove) {
        const sourceId = `ortho-source-${id}`;
        const layerId = `ortho-layer-${id}`;
        try { if (map.getLayer(layerId)) map.removeLayer(layerId); } catch { /* context lost */ }
        try { if (map.getSource(sourceId)) map.removeSource(sourceId); } catch { /* context lost */ }
        previousIds.delete(id);
        orthoUrlsRef.current.delete(id);
      }

      // Date filter state for orthophotos
      const oDatesSet = new Set(useProjectStore.getState().visibleDates);
      const oHasDates = useProjectStore.getState().availableDates.length > 0;

      // Update or add orthophotos
      orthophotos.forEach((ortho) => {
        const sourceId = `ortho-source-${ortho.id}`;
        const layerId = `ortho-layer-${ortho.id}`;
        const existingSource = map.getSource(sourceId) as mapboxgl.ImageSource | undefined;
        const previousUrl = orthoUrlsRef.current.get(ortho.id);

        // Effective visibility: user toggle AND date filter
        const oDateVisible = !oHasDates || !ortho.captureDate || oDatesSet.has(captureDay(ortho.captureDate));
        const oEffectiveVisible = ortho.visible && oDateVisible;

        // Determine render mode: raster tiles (full res) vs image source
        // (downsampled preview). The ortho under adjustment renders as an
        // image so the drag handles can move it live; tiles restore on exit.
        const useTiles = ortho.tileStatus === 'ready' && !!ortho.tilesetId && ortho.id !== editingOrthoId;
        const trackingKey = ortho.url + (useTiles ? `#tile:${ortho.tilesetId}` : '');
        const prevMode = tileSourceModeRef.current.get(`ortho-${ortho.id}`);
        const modeChanged = useTiles ? prevMode !== 'tile' : prevMode === 'tile';

        if (existingSource && previousUrl === trackingKey && !modeChanged) {
          // Source exists with same URL and mode — update in-place (no GPU churn)
          if (!useTiles) {
            try {
              (existingSource as any).setCoordinates(ortho.coordinates);
            } catch (_e) { /* ignore */ }
          }

          if (map.getLayer(layerId)) {
            try {
              map.setPaintProperty(layerId, 'raster-opacity', oEffectiveVisible ? ortho.opacity / 100 : 0);
              map.setLayoutProperty(layerId, 'visibility', oEffectiveVisible ? 'visible' : 'none');
            } catch (_e) { /* ignore */ }
          }
        } else {
          // New source, URL changed, or mode changed (preview→tiles) — (re)create
          try {
            if (map.getLayer(layerId)) map.removeLayer(layerId);
            if (map.getSource(sourceId)) map.removeSource(sourceId);

            if (useTiles) {
              // Full-resolution raster tile source
              const tileUrl = buildTileUrl(ortho.tilesetId!);
              map.addSource(sourceId, {
                type: 'raster',
                tiles: [tileUrl],
                tileSize: 512,
                bounds: [ortho.bounds.minLng, ortho.bounds.minLat, ortho.bounds.maxLng, ortho.bounds.maxLat] as [number, number, number, number],
                minzoom: ortho.tileMinZoom ?? 10,
                maxzoom: ortho.tileMaxZoom ?? 22,
              });
            } else {
              // Downsampled image source — sign /api/v2/assets/ URLs (see overlay effect).
              const imageUrl = signedMap.get(ortho.url) ?? ortho.url;
              map.addSource(sourceId, {
                type: 'image',
                url: imageUrl,
                coordinates: ortho.coordinates,
              });
            }

            map.addLayer({
              id: layerId,
              type: 'raster',
              source: sourceId,
              paint: {
                'raster-opacity': oEffectiveVisible ? ortho.opacity / 100 : 0,
                'raster-fade-duration': 0,
              },
              layout: {
                visibility: oEffectiveVisible ? 'visible' : 'none',
              },
            });

            orthoUrlsRef.current.set(ortho.id, trackingKey);
            tileSourceModeRef.current.set(`ortho-${ortho.id}`, useTiles ? 'tile' : 'image');
            previousIds.add(ortho.id);

            // Keep overlay PNG layers and draw (indication) layers on top of newly added ortho
            ensureLayerOrder(map);
          } catch (err) {
            console.error('[MapCanvas] Failed to add orthophoto layer:', ortho.id, err);
          }
        }
      });

      // Re-stack after every pass so a pure reorder (which hits the in-place
      // update branch above and never re-adds a layer) still reflects the new
      // orthophotos[] order. ensureLayerOrder reads the store array as the
      // source of truth for stacking. Cheap + idempotent.
      ensureLayerOrder(map);
    })();

    return () => { cancelled = true; };
  }, [orthophotos, isMapReady, visibleDates, editingOrthoId]);

  // Update user location on map
  const userLocation = useMapStore((state) => state.userLocation);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;

    const source = map.getSource('user-location') as mapboxgl.GeoJSONSource;
    if (!source) return;

    if (userLocation) {
      // Heading cone shows only while actually moving — a stale GPS course
      // while standing still points the wrong way, so hide it instead.
      const hasHeading = userLocation.heading != null && (userLocation.speed ?? 0) >= 0.5;

      source.setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [userLocation.lng, userLocation.lat],
            },
            properties: {
              heading: userLocation.heading ?? 0,
              hasHeading,
            },
          },
        ],
      });

      // Ground-true accuracy circle: a zoom-interpolated radius expression
      // (doubles per zoom level) instead of a fixed pixel value frozen at the
      // zoom of the last fix. Mapbox GL world = 512 * 2^zoom px.
      const mPerPxAtZ0 = (40075016.686 * Math.cos((userLocation.lat * Math.PI) / 180)) / 512;
      const pxAtZ0 = userLocation.accuracy / mPerPxAtZ0;
      map.setPaintProperty('user-location-accuracy', 'circle-radius', [
        'interpolate', ['exponential', 2], ['zoom'],
        0, pxAtZ0,
        24, pxAtZ0 * Math.pow(2, 24),
      ]);
    } else {
      source.setData({
        type: 'FeatureCollection',
        features: [],
      });
    }
  }, [userLocation, isMapReady]);

  // Follow mode: ease the camera to each fix, keeping the user's zoom. Skip
  // while the map is already moving — that's either a user gesture (never
  // fight fingers) or an in-flight ease/flyTo; the next fix re-centers anyway.
  const isFollowing = useMapStore((state) => state.isFollowing);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady || !isFollowing || !userLocation) return;
    if (map.isMoving()) return;
    map.easeTo({ center: [userLocation.lng, userLocation.lat], duration: 800 });
  }, [userLocation, isFollowing, isMapReady]);

  // A manual drag breaks follow. Mapbox fires dragstart only for user drags,
  // never for easeTo/flyTo camera animations, so no originalEvent check needed.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;

    const breakFollow = () => {
      const s = useMapStore.getState();
      if (s.isFollowing) s.setFollowing(false);
    };
    map.on('dragstart', breakFollow);
    return () => {
      try { map.off('dragstart', breakFollow); } catch { /* map removed */ }
    };
  }, [isMapReady]);

  // Guide line: user dot → target, redrawn on every fix / target change.
  // Target-only (no fix yet) still shows the destination pin.
  const guideTarget = useMapStore((state) => state.guideTarget);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;

    const source = map.getSource('guide') as mapboxgl.GeoJSONSource;
    if (!source) return;

    const features: GeoJSON.Feature[] = [];
    if (guideTarget) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [guideTarget.lng, guideTarget.lat] },
        properties: {},
      });
      if (userLocation) {
        features.push({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [userLocation.lng, userLocation.lat],
              [guideTarget.lng, guideTarget.lat],
            ],
          },
          properties: {},
        });
      }
    }
    source.setData({ type: 'FeatureCollection', features });
  }, [guideTarget, userLocation, isMapReady]);

  // Handle click for add mode
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;

    const activateAt = (lngLat: mapboxgl.LngLat) => {
      const uiState = useUIStore.getState();

      // Guide-to-point mode: the next click sets the field-navigation target.
      // Checked first so it can never create an indication by accident.
      if (useMapStore.getState().guidePointMode) {
        useMapStore.getState().setGuideTarget({
          lng: lngLat.lng,
          lat: lngLat.lat,
          label: 'Map point',
        });
        return;
      }

      // Layout anchor mode (split-screen: PDF click → map click → anchor pair)
      // Also handles overlay anchor and photo anchor registration modes (same event)
      if (uiState.layoutAnchorMode || uiState.overlayAnchorMode || uiState.photoAnchorMode) {
        window.dispatchEvent(new CustomEvent('layout-anchor-map-click', {
          detail: { lng: lngLat.lng, lat: lngLat.lat },
        }));
        return;
      }

      // Asset place mode (mobile upload → click to place on map)
      if (uiState.assetPlaceCallback) {
        uiState.assetPlaceCallback({ lng: lngLat.lng, lat: lngLat.lat });
        return;
      }

      // If in add mode, create/open an indication at the clicked coordinates
      if (uiState.isAddMode) {
        // Quick add: a pre-selected type creates the indication immediately —
        // no popup. Gated on addModeType === null so it can never fire during
        // dataPoint placement (setAddMode also clears the quick type there).
        if (uiState.quickAddIndicationType && uiState.addModeType === null) {
          const id = quickCreateIndication(
            [lngLat.lng, lngLat.lat],
            uiState.quickAddIndicationType,
          );
          if (id) {
            // Short toast — below the indication-layer zoom threshold this is
            // the only visible confirmation. No direct_select of the new
            // feature: drag handles would sit under the next rapid click.
            uiState.addNotification({
              type: 'success',
              title: `${INDICATION_TYPES[uiState.quickAddIndicationType].label} added`,
              duration: 1500,
            });
            // FL-3a: field visitors get the new mark's card immediately (and
            // any pending photo auto-attaches). No-op for operators.
            handleFieldQuickAddCreated(id);
          }
          // NOTE: Do NOT exit add mode here - stay armed for next click
          return;
        }

        uiState.openModal('addIndication', {
          coordinates: [lngLat.lng, lngLat.lat],
        });
        // NOTE: Do NOT exit add mode here - stay armed for next click
        return;
      }
    };

    // Hybrid touch+mouse devices can fire both the tap fallback below AND a
    // real click for one gesture — the tap stamps this and click dedupes.
    let lastTapActivate = 0;

    // FL-2 mobile fix: mapbox-gl-draw preventDefaults touchstart
    // (events.js:107), which suppresses the browser's synthesized click — so
    // on phones Mapbox GL never emits 'click' for a tap on EMPTY map, and
    // every click-driven mode (armed quick-add, guide-to-point, anchor
    // placement) silently did nothing. Recognize the tap ourselves from the
    // map's touch events and run the same activation. Movement/time
    // thresholds keep pans, pinches, and long-presses out.
    //
    // Double-tap zoom disambiguation (share/field sessions only). Mapbox's
    // own tap-zoom recognizer pairs two taps when their START points are
    // within MAX_DIST and the gesture completes inside MAX_TAP_INTERVAL, so
    // this recognizer mirrors those exact rules — a mismatch either places a
    // mark during a zoom (window too short) or swallows a deliberate tap
    // Mapbox would never pair (window too greedy).
    //
    // Rules, in order of what the state machine guarantees:
    //  - A qualifying tap is HELD (not fired) until the pairing window closes.
    //  - The pairing verdict is reached on the second tap's touchEND, which
    //    always lands before the hold expires — so the decision never races.
    //  - A tap that does NOT pair with the held one FLUSHES it immediately
    //    (both taps count); only a genuine pair discards both.
    //  - Anything that is not a qualifying tap (pan, pinch, long-press) leaves
    //    the held tap alone: Mapbox will not zoom for it, so the mark stands.
    //  - A completed pair resets the pairing state, so tap 3 of a rapid
    //    series starts fresh instead of being swallowed.
    const MAPBOX_TAP_INTERVAL_MS = 500;
    const MAPBOX_TAP_MAX_DIST_PX = 30;
    // Hold a hair past the window so a boundary-landing second tap still
    // reaches its verdict first.
    const TAP_HOLD_MS = MAPBOX_TAP_INTERVAL_MS + 20;

    let heldTap: {
      lngLat: mapboxgl.LngLat;
      start: { x: number; y: number };
      at: number;
      timer: number;
    } | null = null;
    const clearHeldTap = () => {
      if (heldTap !== null) {
        window.clearTimeout(heldTap.timer);
        heldTap = null;
      }
    };
    const fireHeldTap = () => {
      if (heldTap === null) return;
      const { lngLat } = heldTap;
      clearHeldTap();
      lastTapActivate = Date.now();
      activateAt(lngLat);
    };
    const holdTap = (lngLat: mapboxgl.LngLat, start: { x: number; y: number }) => {
      clearHeldTap();
      heldTap = {
        lngLat,
        start,
        at: Date.now(),
        timer: window.setTimeout(fireHeldTap, TAP_HOLD_MS),
      };
    };

    const handleClick = (e: mapboxgl.MapMouseEvent) => {
      // A synthesized click for a touch tap the recognizer is already
      // handling — the touch path owns the double-tap decision.
      if (heldTap !== null) return;
      if (Date.now() - lastTapActivate < 500) return;
      // A tap deliberately swallowed as the second half of a double-tap
      // stamps no activation, so on hybrid devices that still synthesize a
      // click for a tap this is what keeps the zoom from placing a mark.
      if (Date.now() - lastSwallowedTapAt < MAPBOX_TAP_INTERVAL_MS) return;
      activateAt(e.lngLat);
    };

    let touchStart: { x: number; y: number; t: number } | null = null;
    let lastSwallowedTapAt = 0;
    const handleTouchStart = (e: mapboxgl.MapTouchEvent) => {
      // Deliberately does NOT touch heldTap: whether this gesture is the
      // second half of a double-tap is only knowable at its touchend, and
      // cancelling here would let a pan or pinch erase a legitimate mark.
      touchStart = e.originalEvent.touches.length === 1
        ? { x: e.point.x, y: e.point.y, t: Date.now() }
        : null;
    };
    const handleTouchEnd = (e: mapboxgl.MapTouchEvent) => {
      const start = touchStart;
      touchStart = null;
      if (!start) return;
      if (e.originalEvent.touches.length > 0) return; // another finger still down
      const dx = e.point.x - start.x;
      const dy = e.point.y - start.y;
      // Movement gate matches Mapbox's own tap tolerance: a gesture Mapbox
      // counts as a tap must be seen here too, or its partner would look
      // like a lone tap and place a mark mid-zoom.
      if (
        dx * dx + dy * dy > MAPBOX_TAP_MAX_DIST_PX * MAPBOX_TAP_MAX_DIST_PX ||
        Date.now() - start.t > MAPBOX_TAP_INTERVAL_MS
      ) {
        return; // pan / long-press — not a tap, and the held tap stands
      }

      // The hold is a share/field-session behavior: techs navigate by
      // double-tap zoom with quick-add armed. Operators keep instant
      // armed-tap placement — rapid consecutive placements are a marking
      // workflow there, and delaying every one is the worse trade.
      if (!isShareViewerRef.current) {
        lastTapActivate = Date.now();
        activateAt(e.lngLat);
        return;
      }

      // Mapbox measures the interval end-to-end and the distance between the
      // two taps' START points; the hold outlives the window by 20 ms, so
      // check the interval explicitly rather than relying on the timer.
      // Swallowing is only ever right when Mapbox will actually zoom:
      // gl-draw's clickOnFeature disables doubleClickZoom (which covers the
      // touch tap-zoom handler) while a feature is selected, and swallowing
      // a pair then would eat both taps for no zoom at all.
      const pairs =
        heldTap !== null &&
        map.doubleClickZoom.isEnabled() &&
        Date.now() - heldTap.at < MAPBOX_TAP_INTERVAL_MS &&
        (start.x - heldTap.start.x) ** 2 + (start.y - heldTap.start.y) ** 2
          < MAPBOX_TAP_MAX_DIST_PX * MAPBOX_TAP_MAX_DIST_PX;
      if (pairs) {
        // Mapbox is zooming: discard both taps and reset, so a third tap is
        // judged on its own rather than pairing with this one.
        clearHeldTap();
        lastSwallowedTapAt = Date.now();
        return;
      }
      // Not a pair — the held tap can no longer become one, so it counts.
      fireHeldTap();
      holdTap(e.lngLat, { x: start.x, y: start.y });
    };

    map.on('click', handleClick);
    map.on('touchstart', handleTouchStart);
    map.on('touchend', handleTouchEnd);

    return () => {
      clearHeldTap();
      map.off('click', handleClick);
      map.off('touchstart', handleTouchStart);
      map.off('touchend', handleTouchEnd);
    };
  }, [isMapReady]);

  // Flight path click + hover handlers
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;

    const fpLayers = ['flight-paths-line', 'flight-path-start', 'flight-path-end'];

    const handleFpClick = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
      if (useUIStore.getState().isAddMode) return;
      const feature = e.features?.[0];
      if (!feature?.properties) return;

      // Resolve flight path ID (line uses 'id', endpoints use 'parentId')
      const fpId = feature.properties.parentId || feature.properties.id;
      if (!fpId) return;

      e.originalEvent.stopPropagation();
      setFpPopup({ id: fpId, lngLat: e.lngLat });
    };

    const handleFpMouseEnter = () => {
      map.getCanvas().style.cursor = 'pointer';
    };
    const handleFpMouseLeave = () => {
      if (!useUIStore.getState().isAddMode) {
        map.getCanvas().style.cursor = '';
      }
    };

    for (const layer of fpLayers) {
      if (map.getLayer(layer)) {
        map.on('click', layer, handleFpClick);
        map.on('mouseenter', layer, handleFpMouseEnter);
        map.on('mouseleave', layer, handleFpMouseLeave);
      }
    }

    return () => {
      // map.remove() may have already run if EditorPage is unmounting (e.g.
      // SPA nav to /settings/team); map.getLayer() then dereferences a null
      // style and throws "Cannot read properties of undefined (reading
      // 'getOwnLayer')", which propagates out of the cleanup and breaks the
      // next route's render. Same hazard as lines 1009/1037/1109 above.
      for (const layer of fpLayers) {
        try {
          if (map.getLayer(layer)) {
            map.off('click', layer, handleFpClick);
            map.off('mouseenter', layer, handleFpMouseEnter);
            map.off('mouseleave', layer, handleFpMouseLeave);
          }
        } catch { /* map removed */ }
      }
    };
  }, [isMapReady]);

  // ── Detection ghosts: reactive source sync from detectionStore ──
  // Ghosts = state 'ghost' AND score01 >= threshold; accepted always render
  // (restyled, so accept never reads as delete below the indication zoom
  // gate); rejected are dropped (dims/hides). Accepted candidates also emit a
  // centroid Point that drives the ✓ symbol layer. Non-privileged / share
  // sessions get an empty collection.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;
    const source = map.getSource('detection-candidates') as mapboxgl.GeoJSONSource | undefined;
    if (!source) return;

    const features: GeoJSON.Feature[] = [];
    if (canReviewGhosts) {
      for (const c of detectionCandidates) {
        if (c.state === 'rejected') continue;
        if (c.state === 'ghost' && c.props.score01 < detectionThreshold) continue;
        features.push({
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [c.ring] },
          properties: { candId: c.id, state: c.state, score01: c.props.score01 },
        });
        if (c.state === 'accepted') {
          const ctr = centroidOfPolygon(c.ring);
          if (ctr) {
            features.push({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: ctr },
              properties: { candId: c.id, state: c.state },
            });
          }
        }
      }
    }
    source.setData({ type: 'FeatureCollection', features });
  }, [detectionCandidates, detectionThreshold, canReviewGhosts, isMapReady]);

  // ── Detection ghosts: hover feature-state + click → ghost popup ──
  // Registered alongside (not inside) the existing indication click path:
  // MapboxDraw owns clicks on real indications via draw.selectionchange, and
  // the guard below yields to any gl-draw feature under the cursor so a ghost
  // overlapping a real indication never double-opens.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady || !canReviewGhosts) return;

    let hoveredId: string | null = null;
    const clearHover = () => {
      if (hoveredId !== null) {
        try {
          map.setFeatureState({ source: 'detection-candidates', id: hoveredId }, { hover: false });
        } catch { /* source swapped */ }
        hoveredId = null;
      }
    };

    const handleGhostMove = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
      const candId = e.features?.[0]?.properties?.candId as string | undefined;
      if (candId === hoveredId) return;
      clearHover();
      if (candId) {
        hoveredId = candId;
        try {
          map.setFeatureState({ source: 'detection-candidates', id: candId }, { hover: true });
        } catch { /* source swapped */ }
      }
      map.getCanvas().style.cursor = 'pointer';
    };

    const handleGhostLeave = () => {
      clearHover();
      if (!useUIStore.getState().isAddMode) {
        map.getCanvas().style.cursor = '';
      }
    };

    const handleGhostClick = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
      // Never fire during any armed placement/selection mode — those clicks
      // belong to their modes (mirrors the flight-path handler's isAddMode
      // guard, widened to every armed mode the generic handler serves).
      const ui = useUIStore.getState();
      if (
        ui.isAddMode ||
        ui.batchStatusMode ||
        ui.layoutAnchorMode ||
        ui.overlayAnchorMode ||
        ui.photoAnchorMode ||
        ui.assetPlaceCallback
      ) return;
      const ms = useMapStore.getState();
      if (ms.areaSelectMode || ms.guidePointMode || lassoFinishingRef.current) return;

      // Yield to a real indication under the cursor — Draw's own click path
      // (selection + preview popup) must win, unregressed.
      const hits = map.queryRenderedFeatures(e.point);
      if (hits.some((f) => f.layer?.id?.startsWith('gl-draw-'))) return;

      const candId = e.features?.[0]?.properties?.candId as string | undefined;
      if (!candId) return;
      const cand = useDetectionStore.getState().candidates.find((c) => c.id === candId);
      if (!cand) return;

      if (cand.state === 'accepted') {
        // Below the indication zoom gate the accepted mark is the only visible
        // artifact — route the click to the REAL indication's preview.
        if (cand.acceptedIndicationId) {
          ms.setPreviewIndication(cand.acceptedIndicationId);
        }
        return;
      }
      useMapStore.getState().setDetectionPopupCand(candId);
    };

    map.on('mousemove', 'detection-ghost-fill', handleGhostMove);
    map.on('mouseleave', 'detection-ghost-fill', handleGhostLeave);
    map.on('click', 'detection-ghost-fill', handleGhostClick);

    return () => {
      // Same unmount hazard as the flight-path handlers above: map.remove()
      // may already have run during SPA nav — guard the teardown.
      try {
        clearHover();
        map.off('mousemove', 'detection-ghost-fill', handleGhostMove);
        map.off('mouseleave', 'detection-ghost-fill', handleGhostLeave);
        map.off('click', 'detection-ghost-fill', handleGhostClick);
      } catch { /* map removed */ }
    };
  }, [isMapReady, canReviewGhosts]);

  // Close the ghost popup when its candidate leaves the visible ghost set
  // (rejected/accepted elsewhere, threshold raised past its score, scan
  // dismissed/cleared, or privileges changed).
  useEffect(() => {
    if (!ghostPopupCandId) return;
    const c = detectionCandidates.find((x) => x.id === ghostPopupCandId);
    if (
      !canReviewGhosts ||
      !c ||
      c.state === 'rejected' ||
      (c.state === 'ghost' && c.props.score01 < detectionThreshold)
    ) {
      setDetectionPopupCand(null);
    }
  }, [detectionCandidates, detectionThreshold, canReviewGhosts, ghostPopupCandId, setDetectionPopupCand]);

  // Track Ctrl key for multi-select
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Control') {
        ctrlKeyRef.current = true;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Control') {
        ctrlKeyRef.current = false;
      }
    };

    // Also handle window blur to reset Ctrl state
    const handleBlur = () => {
      ctrlKeyRef.current = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Change cursor based on add mode, asset place mode, batch status paint mode,
  // or guide-to-point mode
  const isAddMode = useUIStore((state) => state.isAddMode);
  const assetPlaceCallback = useUIStore((state) => state.assetPlaceCallback);
  const batchStatusMode = useUIStore((state) => state.batchStatusMode);
  const guidePointMode = useMapStore((state) => state.guidePointMode);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;

    if (isAddMode || assetPlaceCallback || batchStatusMode || guidePointMode) {
      map.getCanvas().style.cursor = 'crosshair';
    } else {
      map.getCanvas().style.cursor = '';
    }
  }, [isAddMode, assetPlaceCallback, batchStatusMode, guidePointMode, isMapReady]);

  const isLocationTracking = useMapStore((state) => state.isLocationTracking);
  const setFollowing = useMapStore((state) => state.setFollowing);

  return (
    <div className="map-canvas">
      <div ref={mapContainerRef} className="map-canvas__container" />
      {fpPopup && (
        <FlightPathPopup
          flightPathId={fpPopup.id}
          onClose={() => setFpPopup(null)}
        />
      )}
      {dpPopup && (
        <MapPointDetailPopup
          dpId={dpPopup.dpId}
          position={dpPopup.position}
          onClose={() => setDpPopup(null)}
        />
      )}
      {ghostPopupCandId && canReviewGhosts && mapRef.current && (
        <DetectionGhostPopup
          key={ghostPopupCandId}
          candId={ghostPopupCandId}
          map={mapRef.current}
          onClose={() => setDetectionPopupCand(null)}
        />
      )}
      {isLocationTracking && !isFollowing && (
        <button
          className="map-canvas__recenter"
          onClick={() => setFollowing(true)}
          title="Re-center on my location"
          aria-label="Re-center on my location"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />
            <circle cx="12" cy="12" r="8" />
            <line x1="12" y1="1" x2="12" y2="4" />
            <line x1="12" y1="20" x2="12" y2="23" />
            <line x1="1" y1="12" x2="4" y2="12" />
            <line x1="20" y1="12" x2="23" y2="12" />
          </svg>
        </button>
      )}
      <GuideHud />
      <LocationSearch />
    </div>
  );
}
